import { useState, useEffect, useCallback, useRef } from 'react';
import LocationSelect from '../components/shared/LocationSelect.jsx';
import { apiFetch, useApi, getToken } from '../hooks/useApi.js';
import { SupplierApprovalModal, UploadCertificateModal, buildCertAuditNote } from './Salsa.jsx';
import { fmtAbv } from '../utils/format.js';
import PrintButton from '../components/shared/PrintButton.jsx';
import ClientSelect from '../components/shared/ClientSelect.jsx';
import SampleParcelSection from '../components/shared/SampleParcel.jsx';
import { sendToPrinter, usePrintStatus } from '../hooks/usePrint.js';
import { uiConfirm } from '../utils/dialogs.js';
import SimilarStockItems from '../components/shared/SimilarStockItems.jsx';

// ── Shared UI primitives ──────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = {
    borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none',
    fontSize: size === 'sm' ? 12 : 14,
    padding: size === 'sm' ? '5px 12px' : '9px 18px',
    opacity: props.disabled ? 0.5 : 1,
    transition: 'opacity .15s',
  };
  const v = {
    primary: { background: 'var(--pld-green)', color: '#fff' },
    ghost:   { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' },
    danger:  { background: '#dc2626', color: '#fff' },
  };
  return <button {...props} style={{ ...base, ...v[variant], ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 520, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />;
}

function SelectEl({ children, ...props }) {
  return <select {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }}>{children}</select>;
}

// VAT rate for a PO line (Joe, 30 Oct 2026): blank = the PO's own rate.
const VAT_RATES = ['20', '5', '0'];
function VatSelect({ value, onChange, blankLabel = 'PO rate', ...props }) {
  const v = value === null || value === undefined ? '' : String(Number(value));
  return (
    <SelectEl value={v} onChange={e => onChange(e.target.value)} {...props}>
      <option value="">{blankLabel}</option>
      {[...new Set([...VAT_RATES, ...(v && !VAT_RATES.includes(v) ? [v] : [])])].map(r => <option key={r} value={r}>{r}%</option>)}
    </SelectEl>
  );
}

function SectionHeader({ label, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{label}</h4>
      {action}
    </div>
  );
}

// ── Category config ───────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'raw_material',    label: 'Raw Material',    bg: '#fef3c7', text: '#92400e' },
  { value: 'semi_product',    label: 'Semi Product',    bg: '#fde68a', text: '#78350f' },
  { value: 'bulk_finished',   label: 'Bulk Finished',   bg: '#dbeafe', text: '#1e40af' },
  { value: 'bottle_finished', label: 'Bottle Finished', bg: '#ede9fe', text: '#5b21b6' },
  { value: 'packaging',       label: 'Packaging',       bg: '#f3f4f6', text: '#374151' },
  { value: 'consumable',      label: 'Consumable',      bg: '#e0f2fe', text: '#0369a1' },
  { value: 'by_product',      label: 'By-product',      bg: '#ffedd5', text: '#9a3412' },
  { value: 'client_stock',    label: 'Client Stock',    bg: '#f0fdf4', text: '#166534' },
  { value: 'service',         label: 'Service',         bg: '#f1f5f9', text: '#475569' },
];

const COMMODITY_TYPES = [
  { value: 'scotch_whisky',  label: 'Scotch Whisky' },
  { value: 'irish_whiskey',  label: 'Irish Whiskey' },
  { value: 'other_spirits',  label: 'Other Spirits' },
];

const UNITS = ['litres', 'kg', 'units', 'bottles', 'casks', 'ml', 'g'];

const MOVEMENT_LABELS = {
  INTAKE_UK:          'Intake (UK)',
  INTAKE_IMPORT:      'Intake (Import)',
  INTAKE_EU:          'Intake (EU)',
  INTAKE_OTHER:       'Intake (Other)',
  GAINS_STORAGE:      'Storage Gain',
  PRODUCTION_CREATE:  'Production Create',
  DUTY_RELEASE:       'Duty Release',
  EXPORT:             'Export',
  TRANSFER_OUT:       'Transfer Out',
  TRANSFER_IN:        'Transfer In',
  TRANSFER_EU:        'Transfer EU',
  DUTY_FREE:          'Duty Free',
  LOSSES_STORAGE:     'Storage Loss',
  PRODUCTION_LOSS:    'Production Loss',
  DESTRUCTION:        'Destruction',
  PRODUCTION_INPUT:   'Production Input',
  PRODUCTION_OUTPUT:  'Production Output',
  PRODUCTION_BYPRODUCT: 'Production By-product',
  STOCK_ADJUSTMENT:   'Stock Adjustment',
  WRITE_OFF:          'Write-off',
  SAMPLE_REMOVAL:     'Sample Removal',
  RETURN_FROM_CLIENT: 'Return from Client',
  ADJUSTMENT_UP:      'Adjustment Up',
  ADJUSTMENT_DOWN:    'Adjustment Down',
  INITIAL_INTAKE:     'Initial Intake',
  DISPATCH:           'Despatched (order)',
};

const INBOUND = new Set([
  'INTAKE_UK','INTAKE_IMPORT','INTAKE_EU','INTAKE_OTHER',
  'GAINS_STORAGE','PRODUCTION_CREATE','PRODUCTION_OUTPUT',
  'PRODUCTION_BYPRODUCT','TRANSFER_IN','RETURN_FROM_CLIENT',
  'ADJUSTMENT_UP','INITIAL_INTAKE',
]);

function catInfo(value) {
  return CATEGORIES.find(c => c.value === value) ?? { label: value, bg: '#f3f4f6', text: '#374151' };
}

function CategoryBadge({ category }) {
  const c = catInfo(category);
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: c.bg, color: c.text, whiteSpace: 'nowrap' }}>
      {c.label}
    </span>
  );
}

function Pill({ on, children }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: on ? '#dcfce7' : '#f3f4f6', color: on ? '#166534' : '#6b7280' }}>
      {children}
    </span>
  );
}

function fmt(n, dp = 2) {
  if (n == null) return '—';
  return parseFloat(n).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: dp });
}

function fmtDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── SKU Preview ───────────────────────────────────────────────────────────────
function SkuPreview({ category }) {
  const { data } = useApi(category ? `/stock/sku-preview?category=${category}` : null, [category]);
  if (!category || !data?.sku) return null;
  return (
    <div style={{ padding: '7px 11px', borderRadius: 'var(--radius)', background: 'var(--surface-2)', border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-muted)' }}>
      Next SKU: <strong style={{ color: 'var(--text)', fontFamily: 'monospace' }}>{data.sku}</strong>
    </div>
  );
}

// ── Create Item Modal ─────────────────────────────────────────────────────────
function CreateItemModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', category: '', unit: 'litres',
    is_alcoholic: false, commodity_type: '',
    abv: '', litres_per_unit: '',
    reorder_threshold: '', shelf_life_months: '',
    preferred_supplier_id: '', standard_cost: '', minimum_order_quantity: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { data: suppData } = useApi('/suppliers');
  const suppliers = suppData?.suppliers ?? [];
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const [allowDup, setAllowDup] = useState(false);
  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const body = {
        name: form.name.trim(),
        category: form.category,
        unit: form.unit,
        is_alcoholic: form.is_alcoholic,
        commodity_type: form.is_alcoholic ? (form.commodity_type || null) : null,
        abv: form.is_alcoholic && form.abv !== '' ? parseFloat(form.abv) : null,
        litres_per_unit: form.is_alcoholic && form.litres_per_unit !== '' ? parseFloat(form.litres_per_unit) : null,
        reorder_threshold: form.reorder_threshold ? parseFloat(form.reorder_threshold) : null,
        shelf_life_months: form.shelf_life_months ? parseInt(form.shelf_life_months) : null,
        preferred_supplier_id: form.preferred_supplier_id || null,
        standard_cost: form.standard_cost !== '' ? parseFloat(form.standard_cost) : null,
        minimum_order_quantity: form.minimum_order_quantity !== '' ? parseFloat(form.minimum_order_quantity) : null,
        allow_duplicate: allowDup,
      };
      const res = await apiFetch('/stock', { method: 'POST', body });
      onCreated(res.stock_item);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <Modal title="New Stock Item" onClose={onClose} width={560}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Name *">
          <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Grain Spirit 96%" required autoFocus />
        </Field>
        {/* Anything already in stock that looks like the same thing (Joe, 3 Nov 2026). */}
        <SimilarStockItems name={form.name} />
        {/already in stock/.test(error) && (
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={allowDup} onChange={e => setAllowDup(e.target.checked)} /> It really is a different item — add it anyway
          </label>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Category *">
            <SelectEl value={form.category} onChange={e => set('category', e.target.value)} required>
              <option value="">— select —</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </SelectEl>
          </Field>
          <Field label="Unit *">
            <SelectEl value={form.unit} onChange={e => set('unit', e.target.value)}>
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </SelectEl>
          </Field>
        </div>

        {form.category && <SkuPreview category={form.category} />}

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_alcoholic} onChange={e => set('is_alcoholic', e.target.checked)} />
            Alcoholic
          </label>
          {form.is_alcoholic && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Received stock starts in bond (or duty paid if received that way) and stays so until a duty release or despatch — wherever it's stored.
            </span>
          )}
        </div>

        {form.is_alcoholic && (
          <Field label="Commodity Type">
            <SelectEl value={form.commodity_type} onChange={e => set('commodity_type', e.target.value)}>
              <option value="">— none —</option>
              {COMMODITY_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </SelectEl>
          </Field>
        )}

        {form.is_alcoholic && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="ABV %" hint="Needed to work out litres of pure alcohol — without it, gains/losses on this item won't show on the W1 return">
              <Input type="number" min="0" max="100" step="0.01" value={form.abv} onChange={e => set('abv', e.target.value)} placeholder="e.g. 38" />
            </Field>
            {form.unit !== 'litres' && (
              <Field label={`Litres per ${form.unit || 'unit'}`} hint="e.g. 0.7 for a 70cl bottle">
                <Input type="number" min="0" step="0.0001" value={form.litres_per_unit} onChange={e => set('litres_per_unit', e.target.value)} placeholder="e.g. 0.7" />
              </Field>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Reorder Threshold" hint="Quantity that triggers a low-stock alert">
            <Input type="number" min="0" step="0.001" value={form.reorder_threshold} onChange={e => set('reorder_threshold', e.target.value)} placeholder="e.g. 100" />
          </Field>
          <Field label="Shelf Life (months)" hint="Leave blank for indefinite">
            <Input type="number" min="1" step="1" value={form.shelf_life_months} onChange={e => set('shelf_life_months', e.target.value)} placeholder="e.g. 24" />
          </Field>
        </div>

        <Field label="Preferred Supplier" hint="Used to auto-group this item into the right supplier's PO when purchase orders are generated from recipes">
          <SelectEl value={form.preferred_supplier_id} onChange={e => set('preferred_supplier_id', e.target.value)}>
            <option value="">— none —</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </SelectEl>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Standard Cost" hint="A reference unit price, used for recipe costing and Generate POs when this item has no priced purchase order yet. Real PO prices always take priority over this once they exist.">
            <Input type="number" min="0" step="0.0001" value={form.standard_cost} onChange={e => set('standard_cost', e.target.value)} placeholder="e.g. 4.50" />
          </Field>
          <Field label="Minimum Order Quantity" hint="If this is only sold in a fixed lot size (e.g. 25kg bags), Generate POs will round its recommended order up to a multiple of this">
            <Input type="number" min="0" step="0.001" value={form.minimum_order_quantity} onChange={e => set('minimum_order_quantity', e.target.value)} placeholder="e.g. 25" />
          </Field>
        </div>

        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !form.name.trim() || !form.category}>
            {saving ? 'Creating…' : 'Create Item'}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Edit Item Modal ───────────────────────────────────────────────────────────
function EditItemModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item.name,
    unit: item.unit,
    is_alcoholic: item.is_alcoholic,
    commodity_type: item.commodity_type ?? '',
    abv: item.abv ?? '',
    litres_per_unit: item.litres_per_unit ?? '',
    unlimited_stock: !!item.unlimited_stock,
    reorder_threshold: item.reorder_threshold ?? '',
    shelf_life_months: item.shelf_life_months ?? '',
    is_active: item.is_active,
    preferred_supplier_id: item.preferred_supplier_id ?? '',
    standard_cost: item.standard_cost ?? '',
    minimum_order_quantity: item.minimum_order_quantity ?? '',
    ship_weight_kg: item.ship_weight_kg ?? '',
    ships_individually: !!item.ships_individually,
    owner_client_id: item.owner_type === 'CONTRACT' ? (item.owner_client_id ?? '') : '',
    sugar_pct: item.sugar_pct ?? '', density_kg_l: item.density_kg_l ?? '', solids_l_per_kg: item.solids_l_per_kg ?? '',
  });
  const { data: clientData } = useApi('/clients');
  const clientList = clientData?.clients ?? clientData?.data ?? (Array.isArray(clientData) ? clientData : []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { data: suppData } = useApi('/suppliers');
  const suppliers = suppData?.suppliers ?? [];
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const body = {
        name: form.name.trim(),
        unit: form.unit,
        is_alcoholic: form.is_alcoholic,
        commodity_type: form.is_alcoholic ? (form.commodity_type || null) : null,
        abv: form.is_alcoholic && form.abv !== '' ? parseFloat(form.abv) : null,
        litres_per_unit: form.is_alcoholic && form.litres_per_unit !== '' ? parseFloat(form.litres_per_unit) : null,
        reorder_threshold: form.reorder_threshold ? parseFloat(form.reorder_threshold) : null,
        shelf_life_months: form.shelf_life_months ? parseInt(form.shelf_life_months) : null,
        is_active: form.is_active,
        unlimited_stock: !!form.unlimited_stock,
        preferred_supplier_id: form.preferred_supplier_id || null,
        standard_cost: form.standard_cost !== '' ? parseFloat(form.standard_cost) : null,
        minimum_order_quantity: form.minimum_order_quantity !== '' ? parseFloat(form.minimum_order_quantity) : null,
        ship_weight_kg: form.ship_weight_kg !== '' ? parseFloat(form.ship_weight_kg) : null,
        ships_individually: !!form.ships_individually,
        sugar_pct: form.sugar_pct !== '' ? parseFloat(form.sugar_pct) : null,
        density_kg_l: form.density_kg_l !== '' ? parseFloat(form.density_kg_l) : null,
        solids_l_per_kg: form.solids_l_per_kg !== '' ? parseFloat(form.solids_l_per_kg) : null,
        owner_type: form.owner_client_id ? 'CONTRACT' : 'PLD',
        owner_client_id: form.owner_client_id || null,
      };
      const res = await apiFetch(`/stock/${item.id}`, { method: 'PUT', body });
      onSaved(res.stock_item);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <Modal title={`Edit — ${item.sku}`} onClose={onClose} width={560}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Name *">
          <Input value={form.name} onChange={e => set('name', e.target.value)} required autoFocus />
        </Field>

        <Field label="Unit *">
          <SelectEl value={form.unit} onChange={e => set('unit', e.target.value)}>
            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
          </SelectEl>
        </Field>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_alcoholic} onChange={e => set('is_alcoholic', e.target.checked)} />
            Alcoholic
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
            Active
          </label>
          {form.is_alcoholic && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Bonded by default — duty status is tracked by location, not set here.
            </span>
          )}
        </div>

        {form.is_alcoholic && (
          <Field label="Commodity Type">
            <SelectEl value={form.commodity_type} onChange={e => set('commodity_type', e.target.value)}>
              <option value="">— none —</option>
              {COMMODITY_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </SelectEl>
          </Field>
        )}

        {form.is_alcoholic && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="ABV %" hint="Needed to work out litres of pure alcohol — without it, gains/losses on this item won't show on the W1 return">
              <Input type="number" min="0" max="100" step="0.01" value={form.abv} onChange={e => set('abv', e.target.value)} placeholder="e.g. 38" />
            </Field>
            {form.unit !== 'litres' && (
              <Field label={`Litres per ${form.unit || 'unit'}`} hint="e.g. 0.7 for a 70cl bottle">
                <Input type="number" min="0" step="0.0001" value={form.litres_per_unit} onChange={e => set('litres_per_unit', e.target.value)} placeholder="e.g. 0.7" />
              </Field>
            )}
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.unlimited_stock} onChange={e => set('unlimited_stock', e.target.checked)} style={{ marginTop: 2 }} />
          <span><strong>Unlimited</strong> — made on site as needed (e.g. RO water). It never runs out: using it never fails for lack of stock, and it's never suggested for ordering.</span>
        </label>

        <Field label="Owned by" hint="Client stock can go on that client's fulfilment orders; PLD stock can't">
          <SelectEl value={form.owner_client_id} onChange={e => set('owner_client_id', e.target.value)}>
            <option value="">Pleasant Land Distillery (our own stock)</option>
            {clientList.map(c => <option key={c.id} value={c.id}>{c.name} (client stock)</option>)}
          </SelectEl>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
          <Field label="Shipping weight per unit (kg)" hint="For the DPD file — blank uses the default in Settings">
            <Input type="number" min="0" step="0.001" value={form.ship_weight_kg} onChange={e => set('ship_weight_kg', e.target.value)} placeholder="e.g. 1.4" />
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer', paddingBottom: 10 }}>
            <input type="checkbox" checked={form.ships_individually} onChange={e => set('ships_individually', e.target.checked)} />
            Ships in its own parcel <span style={{ color: 'var(--text-muted)' }}>(otherwise 6 per parcel)</span>
          </label>
        </div>

        {/* For recipe sugar / strength predictions — from the supplier's CoA or spec. */}
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', margin: '4px 0 -4px' }}>Composition <span style={{ textTransform: 'none', fontWeight: 400 }}>— for recipe sugar and strength predictions; from the CoA</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="Sugar / solids (% by weight)" hint="Total sugars g/100 g, or °Brix. 100 for dry sugar">
            <Input type="number" min="0" max="100" step="any" value={form.sugar_pct} onChange={e => set('sugar_pct', e.target.value)} placeholder="e.g. 68" />
          </Field>
          <Field label="Density (kg/L)" hint="Or specific gravity. For syrups and liquids">
            <Input type="number" min="0" step="any" value={form.density_kg_l} onChange={e => set('density_kg_l', e.target.value)} placeholder="e.g. 1.33" />
          </Field>
          <Field label="Dissolved volume (L/kg)" hint="Blank = 0.63 (sucrose)">
            <Input type="number" min="0" max="1.5" step="any" value={form.solids_l_per_kg} onChange={e => set('solids_l_per_kg', e.target.value)} placeholder="0.63" />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Reorder Threshold">
            <Input type="number" min="0" step="0.001" value={form.reorder_threshold} onChange={e => set('reorder_threshold', e.target.value)} />
          </Field>
          <Field label="Shelf Life (months)">
            <Input type="number" min="1" step="1" value={form.shelf_life_months} onChange={e => set('shelf_life_months', e.target.value)} placeholder="Indefinite" />
          </Field>
        </div>

        <Field label="Preferred Supplier" hint="Used to auto-group this item into the right supplier's PO when purchase orders are generated from recipes">
          <SelectEl value={form.preferred_supplier_id} onChange={e => set('preferred_supplier_id', e.target.value)}>
            <option value="">— none —</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </SelectEl>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Standard Cost" hint="A reference unit price, used for recipe costing and Generate POs when this item has no priced purchase order yet. Real PO prices always take priority over this once they exist.">
            <Input type="number" min="0" step="0.0001" value={form.standard_cost} onChange={e => set('standard_cost', e.target.value)} placeholder="e.g. 4.50" />
          </Field>
          <Field label="Minimum Order Quantity" hint="If this is only sold in a fixed lot size (e.g. 25kg bags), Generate POs will round its recommended order up to a multiple of this">
            <Input type="number" min="0" step="0.001" value={form.minimum_order_quantity} onChange={e => set('minimum_order_quantity', e.target.value)} placeholder="e.g. 25" />
          </Field>
        </div>

        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Adjust Stock Modal ────────────────────────────────────────────────────────
const DUTY_WORD = { in_bond: 'in bond', duty_paid: 'duty paid' };

function AdjustStockModal({ item, locations, onClose, onSaved }) {
  // A holding is a location + the stock's duty status there (in bond /
  // duty paid can sit side by side in one location).
  const holdingKey = l => `${l.location_id}|${l.duty_status || ''}`;
  const [form, setForm] = useState({ direction: 'up', quantity: '', location_id: locations[0] ? holdingKey(locations[0]) : '', storage_unit_id: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [source, setSource] = useState('location'); // 'location' | 'pallet'
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // The item's locations often arrive after the modal opens — the dropdown
  // then shows the first one but nothing was actually chosen, leaving Apply
  // greyed out. Pick the first one once they're there.
  useEffect(() => {
    if (locations.length && !locations.some(l => holdingKey(l) === form.location_id)) {
      setForm(f => ({ ...f, location_id: holdingKey(locations[0]) }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  // Pallets tracking this specific item — a pallet is just a labelled
  // handle on a location's stock, so only ones already holding this item
  // are offered here.
  const { data: unitData } = useApi('/storage-units?status=active');
  const pallets = (unitData?.storage_units ?? []).filter(u => u.stock_item_id === item.id);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await apiFetch(`/stock/${item.id}/adjust`, {
        method: 'POST',
        body: {
          direction: form.direction,
          quantity: parseFloat(form.quantity),
          location_id: source === 'location' ? form.location_id.split('|')[0] : undefined,
          duty_status: source === 'location' ? (form.location_id.split('|')[1] || undefined) : undefined,
          storage_unit_id: source === 'pallet' ? form.storage_unit_id : undefined,
          notes: form.notes.trim() || undefined,
        },
      });
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <Modal title="Adjust Stock" onClose={onClose} width={480}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Direction">
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ value: 'up', label: '▲ Add stock', color: '#166534', bg: '#dcfce7' }, { value: 'down', label: '▼ Remove stock', color: '#991b1b', bg: '#fee2e2' }].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => set('direction', opt.value)}
                style={{
                  flex: 1, padding: '9px 12px', borderRadius: 'var(--radius)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  border: form.direction === opt.value ? `2px solid ${opt.color}` : '2px solid var(--border)',
                  background: form.direction === opt.value ? opt.bg : 'var(--surface-2)',
                  color: form.direction === opt.value ? opt.color : 'var(--text)',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label={`Quantity (${item.unit})`}>
          <Input type="number" min="0.001" step="0.001" value={form.quantity} onChange={e => set('quantity', e.target.value)} placeholder="0.000" required autoFocus />
        </Field>

        <Field label="Where from">
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            {[{ value: 'location', label: 'A location' }, { value: 'pallet', label: 'A specific pallet' }].map(opt => (
              <button key={opt.value} type="button" onClick={() => setSource(opt.value)}
                style={{
                  flex: 1, padding: '7px 10px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: source === opt.value ? '2px solid var(--pld-green)' : '2px solid var(--border)',
                  background: source === opt.value ? '#f0fdf4' : 'var(--surface-2)',
                  color: source === opt.value ? 'var(--pld-green)' : 'var(--text)',
                }}>
                {opt.label}
              </button>
            ))}
          </div>

          {source === 'location' ? (
            locations.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>No locations found — stock must exist in at least one location.</p>
            ) : (
              <SelectEl value={form.location_id} onChange={e => set('location_id', e.target.value)} required>
                {locations.map(l => (
                  <option key={holdingKey(l)} value={holdingKey(l)}>
                    {l.location_code}{item.is_alcoholic && DUTY_WORD[l.duty_status] ? ` (${DUTY_WORD[l.duty_status]})` : ''} — {fmt(l.quantity_available)} {item.unit} available
                  </option>
                ))}
              </SelectEl>
            )
          ) : (
            pallets.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>No pallets currently recorded as holding this item.</p>
            ) : (
              <SelectEl value={form.storage_unit_id} onChange={e => set('storage_unit_id', e.target.value)} required>
                <option value="">— Select a pallet —</option>
                {pallets.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.unit_ref} — {u.location_code || 'no location'} ({fmt(u.quantity)} {item.unit} recorded)
                  </option>
                ))}
              </SelectEl>
            )
          )}
        </Field>

        <Field label="Notes">
          <Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Reason for adjustment…" />
        </Field>

        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        {!saving && (() => {
          const why = !(Number(form.quantity) > 0) ? 'Enter a quantity'
            : source === 'location' ? (!form.location_id ? 'Choose a location' : '')
            : (!form.storage_unit_id ? 'Choose a pallet' : '');
          return why ? <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', textAlign: 'right' }}>{why} to apply the adjustment.</p> : null;
        })()}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !(Number(form.quantity) > 0) || (source === 'location' ? !form.location_id : !form.storage_unit_id)}>
            {saving ? 'Saving…' : 'Apply Adjustment'}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Correct duty status ──────────────────────────────────────────────────────
// For stock that was RECORDED wrongly — e.g. the starting status Blake gave
// existing stock when duty status moved onto the stock itself. Not a duty
// event: no duty charged, nothing on the W1. Paying duty is a duty release.
function CorrectDutyModal({ item, holding, onClose, onSaved }) {
  const to = holding.duty_status === 'in_bond' ? 'duty_paid' : 'in_bond';
  const [quantity, setQuantity] = useState(String(parseFloat(holding.quantity_on_hand) || ''));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch(`/stock/${item.id}/correct-duty-status`, {
        method: 'POST',
        body: { location_id: holding.location_id, from_status: holding.duty_status, to_status: to, quantity: parseFloat(quantity), reason },
      });
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }
  return (
    <Modal title="Correct duty status" onClose={onClose} width={460}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Use this only when Blake has the duty status <strong>recorded wrongly</strong> at <code>{holding.location_code}</code>.
          It changes {DUTY_WORD[holding.duty_status]} to <strong>{DUTY_WORD[to]}</strong> without charging duty or touching the W1.
          To actually pay duty on bonded stock, use a duty release.
        </p>
        <Field label={`Quantity (${item.unit})`}>
          <Input type="number" min="0" step="any" max={holding.quantity_on_hand} required value={quantity} onChange={e => setQuantity(e.target.value)} />
        </Field>
        <Field label="Why was it recorded wrongly?">
          <Input required value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. The Pig's stock arrived duty paid" />
        </Field>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !reason.trim() || !(parseFloat(quantity) > 0)}>{saving ? 'Saving…' : `Mark ${DUTY_WORD[to]}`}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Item Detail ───────────────────────────────────────────────────────────────
// ── Material Label Modal ──────────────────────────────────────────────────
// Prints a plain black-and-white 4x6in label (Godex RT700 form factor) via
// the browser's normal print dialog — same mechanism as the pallet label,
// just a different PDF and no colour. Every field beyond name/SKU is
// off by default and added per print: this is a QC/traceability label
// filled in at the moment of printing (a batch, a weight reading, a
// temperature), not something Blake stores against the item permanently.
const LABEL_FIELDS = [
  { key: 'batch', label: 'Batch', placeholder: 'e.g. B2026-0091' },
  { key: 'location', label: 'Location', placeholder: 'e.g. RACKC-BOT-03' },
  { key: 'abv', label: 'ABV (%)', placeholder: 'e.g. 40' },
  { key: 'volume', label: 'Volume (L)', placeholder: 'e.g. 0.7' },
  { key: 'gross_weight', label: 'Gross Weight (kg)', placeholder: '' },
  { key: 'tare_weight', label: 'Tare Weight (kg)', placeholder: '' },
  { key: 'net_weight', label: 'Net Weight (kg)', placeholder: '' },
  { key: 'density', label: 'Density (kg/L)', placeholder: '' },
  { key: 'temperature', label: 'Temperature (°C)', placeholder: '' },
];

function MaterialLabelModal({ item, onClose }) {
  const [enabled, setEnabled] = useState({});
  const [values, setValues] = useState({
    abv: item.abv ?? '',
    volume: item.litres_per_unit ?? '',
  });
  const [printing, setPrinting] = useState(false);
  const [err, setErr] = useState('');

  const toggle = (key) => setEnabled(e => ({ ...e, [key]: !e[key] }));
  const setValue = (key, v) => setValues(vs => ({ ...vs, [key]: v }));

  function selectedFields() {
    const fields = {};
    LABEL_FIELDS.forEach(f => {
      if (enabled[f.key] && values[f.key] !== undefined && values[f.key] !== '') {
        fields[f.key] = values[f.key];
      }
    });
    return fields;
  }

  async function handlePrint() {
    setPrinting(true); setErr('');
    try {
      const fields = selectedFields();
      const token = localStorage.getItem('pld_token');
      const res = await fetch(`/api/stock/${item.id}/material-label.pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ fields }),
      });
      if (!res.ok) throw new Error('Failed to generate label');
      const blob = await res.blob();
      const file = new File([blob], `${item.sku}-material-label.pdf`, { type: 'application/pdf' });
      const w = window.open(URL.createObjectURL(file), '_blank');
      if (w) setTimeout(() => { try { w.print(); } catch (_) {} }, 400);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setPrinting(false);
    }
  }

  return (
    <Modal title={`Print Material Label — ${item.sku}`} onClose={onClose} width={480}>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 14px' }}>
        A plain black-and-white 4x6in label for {item.name}. Tick what to include — nothing here is on
        the label by default. Sized for the Godex RT700; print it to that printer from the dialog that opens.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {LABEL_FIELDS.map(f => (
          <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer', width: 160, flexShrink: 0 }}>
              <input type="checkbox" checked={!!enabled[f.key]} onChange={() => toggle(f.key)} />
              {f.label}
            </label>
            <Input
              disabled={!enabled[f.key]}
              value={values[f.key] ?? ''}
              onChange={e => setValue(f.key, e.target.value)}
              placeholder={f.placeholder}
              style={{ flex: 1 }}
            />
          </div>
        ))}
      </div>
      {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="ghost" onClick={handlePrint} disabled={printing}>{printing ? 'Generating…' : 'Preview'}</Btn>
        <PrintButton docType="material_label" method="POST" path={`/stock/${item.id}/material-label.pdf`}
          getBody={() => ({ fields: selectedFields() })} title={`Material label ${item.sku}`} size="md" />
      </div>
    </Modal>
  );
}

// Sample bottle labels from a stock item: the 100×150 label printer, two
// labels a sheet with a cut line — same as development and production
// samples, headed PRODUCT SAMPLE.
function StockSampleLabelModal({ item, onClose }) {
  const [l, setL] = useState({
    product: item.name || '', client: item.owner_type === 'CONTRACT' ? (item.owner_client_name || '') : '',
    batch: '', abv: item.abv != null ? String(Number(item.abv)) : '', size: '',
    date: new Date().toLocaleDateString('en-GB'), note: '',
  });
  const set = k => e => setL(x => ({ ...x, [k]: e.target.value }));
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const parcelRef = useRef(null);
  const [sending, setSending] = useState(false);
  async function preview() {
    setBusy(true); setErr('');
    const win = window.open('', '_blank');
    try {
      const res = await fetch(`/api/stock/${item.id}/sample-label.pdf`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ top: l }) });
      if (!res.ok) throw new Error(`Couldn't make the label (HTTP ${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (win) win.close(); setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <Modal title="Sample bottle labels" onClose={onClose} width={600}>
      {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-muted)' }}>Prints on the 100×150 label printer as two labels with a cut line halfway down — cut it in half for two bottles. Check the details: this goes to the customer.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Product name"><Input value={l.product} onChange={set('product')} /></Field>
        <Field label="Client"><ClientSelect value={l.client} onChange={(v) => setL(x => ({ ...x, client: v }))} /></Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr .7fr .7fr 1fr', gap: 12 }}>
        <Field label="Batch"><Input value={l.batch} onChange={set('batch')} placeholder="optional" /></Field>
        <Field label="ABV %"><Input value={l.abv} onChange={set('abv')} /></Field>
        <Field label="Size"><Input value={l.size} onChange={set('size')} placeholder="e.g. 5cl" /></Field>
        <Field label="Date"><Input value={l.date} onChange={set('date')} /></Field>
      </div>
      <Field label="Footer"><Input value={l.note} onChange={set('note')} placeholder="Not for sale · for evaluation only" /></Field>
      <SampleParcelSection ref={parcelRef} clientName={l.client} kind="production" onSendChange={setSending}
        reference={item.sku} description={`Samples — ${l.product || item.sku}`.slice(0, 35)} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="ghost" onClick={preview} disabled={busy}>{busy ? 'Making…' : 'Preview'}</Btn>
        <PrintButton docType="stock_sample_label" method="POST" path={`/stock/${item.id}/sample-label.pdf`} getBody={() => ({ top: l })} title={`Sample labels — ${l.product}`.trim()} size="md"
          label={sending ? 'Print & send' : 'Print'} onDone={() => parcelRef.current?.queue()} />
      </div>
    </Modal>
  );
}

function ItemDetail({ itemId, onBack }) {
  const { data, loading, error, refetch } = useApi(`/stock/${itemId}`, [itemId]);
  const item = data?.stock_item;
  const summary = data?.stock_summary;
  const derivedDutyStatus = data?.derived_duty_status;

  const [movements, setMovements] = useState([]);
  const [movTotal, setMovTotal] = useState(0);
  const [movOffset, setMovOffset] = useState(0);
  const [movLoading, setMovLoading] = useState(false);
  const MOV_LIMIT = 20;

  const [showEdit, setShowEdit] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [showSample, setShowSample] = useState(false);
  const [correcting, setCorrecting] = useState(null);

  const loadMovements = useCallback(async (offset = 0) => {
    setMovLoading(true);
    try {
      const res = await apiFetch(`/stock/${itemId}/movements?limit=${MOV_LIMIT}&offset=${offset}`);
      setMovements(res.movements ?? []);
      setMovTotal(res.total ?? 0);
      setMovOffset(offset);
    } catch (_) {}
    setMovLoading(false);
  }, [itemId]);

  useEffect(() => { loadMovements(0); }, [loadMovements]);

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading…</div>;
  if (error || !item) return <div style={{ padding: 32, color: '#dc2626' }}>Failed to load item.</div>;

  const totalOnHand = parseFloat(summary?.totals?.on_hand ?? 0);
  const totalAvail  = parseFloat(summary?.totals?.available ?? 0);
  const totalRes    = parseFloat(summary?.totals?.reserved ?? 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {showEdit && (
        <EditItemModal
          item={item}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); refetch(); }}
        />
      )}
      {showAdjust && (
        <AdjustStockModal
          item={item}
          locations={summary?.locations ?? []}
          onClose={() => setShowAdjust(false)}
          onSaved={() => { setShowAdjust(false); refetch(); loadMovements(0); }}
        />
      )}
      {showLabel && (
        <MaterialLabelModal item={item} onClose={() => setShowLabel(false)} />
      )}
      {showSample && <StockSampleLabelModal item={item} onClose={() => setShowSample(false)} />}
      {correcting && (
        <CorrectDutyModal
          item={item}
          holding={correcting}
          onClose={() => setCorrecting(null)}
          onSaved={() => { setCorrecting(null); refetch(); loadMovements(0); }}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0 }}>←</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--text-muted)', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)' }}>{item.sku}</span>
            <CategoryBadge category={item.category} />
            {!item.is_active && <Pill on={false}>Inactive</Pill>}
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{item.name}</h2>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" size="sm" onClick={() => setShowLabel(true)}>Print Material Label</Btn>
          <Btn variant="ghost" size="sm" onClick={() => setShowSample(true)}>Sample Labels</Btn>
          <Btn variant="ghost" size="sm" onClick={() => setShowAdjust(true)}>Adjust Stock</Btn>
          <Btn variant="ghost" size="sm" onClick={() => setShowEdit(true)}>Edit</Btn>
        </div>
      </div>

      {/* Info cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
        {[
          { label: 'Unit', value: item.unit },
          ...(item.unlimited_stock ? [{ label: 'Stock', value: <Pill on>Unlimited</Pill> }] : []),
          { label: 'Alcoholic', value: <Pill on={item.is_alcoholic}>{item.is_alcoholic ? 'Yes' : 'No'}</Pill> },
          ...(item.is_alcoholic ? [{
            label: 'Duty status',
            value: derivedDutyStatus
              ? (
                <Pill on={derivedDutyStatus === 'bonded'}>
                  {derivedDutyStatus === 'split' ? 'Some in bond, some duty paid' : derivedDutyStatus === 'bonded' ? 'In bond' : 'Duty paid'}
                </Pill>
              )
              : <span style={{ color: 'var(--text-muted)' }}>—</span>,
          }] : []),
          { label: 'Owned by', value: item.owner_type === 'CONTRACT' ? (item.owner_client_name || 'A client') : 'PLD' },
          { label: 'Commodity', value: item.commodity_type ? COMMODITY_TYPES.find(c => c.value === item.commodity_type)?.label ?? item.commodity_type : '—' },
          ...(item.is_alcoholic ? [
            { label: 'ABV', value: item.abv != null ? `${fmtAbv(item.abv)}%` : <span style={{ color: '#dc2626' }}>Not set</span> },
            { label: `Litres / ${item.unit}`, value: item.litres_per_unit != null ? fmt(item.litres_per_unit, 4) : (item.unit === 'litres' ? '—' : <span style={{ color: '#dc2626' }}>Not set</span>) },
          ] : []),
          ...(item.sugar_pct != null || item.density_kg_l != null ? [{ label: 'Composition', value: [item.sugar_pct != null ? `${Number(item.sugar_pct)}% sugar/solids` : null, item.density_kg_l != null ? `${Number(item.density_kg_l)} kg/L` : null, item.solids_l_per_kg != null ? `${Number(item.solids_l_per_kg)} L/kg dissolved` : null].filter(Boolean).join(' · ') }] : []),
          { label: 'Ship weight', value: item.ship_weight_kg != null ? `${fmt(item.ship_weight_kg, 3)} kg${item.ships_individually ? ' · own parcel' : ''}` : (item.ships_individually ? 'Own parcel' : '—') },
          { label: 'Reorder At', value: item.reorder_threshold ? `${fmt(item.reorder_threshold)} ${item.unit}` : '—' },
          { label: 'Shelf Life', value: item.shelf_life_months ? `${item.shelf_life_months} months` : 'Indefinite' },
          { label: 'Preferred Supplier', value: item.preferred_supplier_name || '—' },
          { label: 'Standard Cost', value: item.standard_cost != null ? `£${fmt(item.standard_cost, 4)} / ${item.unit}` : '—' },
          { label: 'Min Order Qty', value: item.minimum_order_quantity != null ? `${fmt(item.minimum_order_quantity)} ${item.unit}` : '—' },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Stock Levels */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
        <SectionHeader label="Stock Levels" />
        {item.unlimited_stock ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Unlimited — made on site as needed, so it isn't counted. Uses still show in the movement history.</p>
        ) : !summary?.locations?.length ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>No stock recorded for this item yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  {['Location', 'Zone', ...(item.is_alcoholic ? ['Duty'] : []), 'On Hand', 'Reserved', 'Available', ...(item.is_alcoholic ? [''] : [])].map(h => (
                    <th key={h} style={{ padding: '6px 10px', textAlign: ['Location', 'Zone', 'Duty'].includes(h) ? 'left' : 'right', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.locations.map(row => (
                  <tr key={`${row.location_id}|${row.duty_status}`} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text)' }}>
                      <span style={{ fontFamily: 'monospace' }}>{row.location_code}</span>
                      {row.units?.length > 0 && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.5 }}>
                          {row.units.map(u => <div key={u.id}>on <a href={`/storage-units?unit=${u.id}`} style={{ color: 'var(--pld-green)', fontWeight: 600 }}>{u.unit_ref}</a>{u.name ? ` (${u.name})` : ''}: {fmt(u.quantity)} {item.unit}</div>)}
                          {Number(row.loose) > 0.0001 && <div>loose: {fmt(row.loose)} {item.unit}</div>}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>{row.zone_name ?? row.zone_type}</td>
                    {item.is_alcoholic && (
                      <td style={{ padding: '8px 10px' }}>
                        <Pill on={row.duty_status === 'in_bond'}>{DUTY_WORD[row.duty_status] || '—'}</Pill>
                      </td>
                    )}
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text)' }}>{fmt(row.quantity_on_hand)} {item.unit}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-muted)' }}>{fmt(row.quantity_reserved)} {item.unit}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: parseFloat(row.quantity_available) > 0 ? '#166534' : '#6b7280' }}>{fmt(row.quantity_available)} {item.unit}</td>
                    {item.is_alcoholic && (
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                        {DUTY_WORD[row.duty_status] && (
                          <button type="button" onClick={() => setCorrecting(row)} title="Fix a wrongly recorded duty status (not a duty release)"
                            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                            Correct status
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                  <td colSpan={item.is_alcoholic ? 3 : 2} style={{ padding: '8px 10px', fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>Total</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{fmt(totalOnHand)} {item.unit}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: 'var(--text-muted)' }}>{fmt(totalRes)} {item.unit}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: totalAvail > 0 ? '#166534' : '#6b7280' }}>{fmt(totalAvail)} {item.unit}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Batches */}
      <BatchesSection itemId={itemId} item={item} />

      {/* Approved Suppliers */}
      <ApprovedSuppliersSection itemId={itemId} unit={item.unit} />

      {/* Supplier Price Breaks */}
      <PriceBreaksSection itemId={itemId} unit={item.unit} />

      {/* Movements */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
        <SectionHeader label={`Movement History${movTotal ? ` (${movTotal})` : ''}`} />
        {movLoading ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Loading…</p>
        ) : !movements.length ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>No movements recorded yet.</p>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    {['Date', 'Type', 'Qty', 'Notes'].map(h => (
                      <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Qty' ? 'right' : 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {movements.map(m => {
                    const inbound = INBOUND.has(m.movement_type);
                    return (
                      <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 10px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: 13 }}>{fmtDate(m.created_at)}</td>
                        <td style={{ padding: '8px 10px', color: 'var(--text)' }}>{MOVEMENT_LABELS[m.movement_type] ?? m.movement_type}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: inbound ? '#166534' : '#dc2626', whiteSpace: 'nowrap' }}>
                          {inbound ? '+' : '−'}{fmt(m.quantity)} {item.unit}
                          {m.abv ? <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 4 }}>{fmtAbv(m.abv)}% ABV</span> : null}
                        </td>
                        <td style={{ padding: '8px 10px', color: 'var(--text-muted)', fontSize: 13 }}>{m.notes ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {movTotal > MOV_LIMIT && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Showing {movOffset + 1}–{Math.min(movOffset + MOV_LIMIT, movTotal)} of {movTotal}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn variant="ghost" size="sm" disabled={movOffset === 0} onClick={() => loadMovements(movOffset - MOV_LIMIT)}>← Prev</Btn>
                  <Btn variant="ghost" size="sm" disabled={movOffset + MOV_LIMIT >= movTotal} onClick={() => loadMovements(movOffset + MOV_LIMIT)}>Next →</Btn>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ── Supplier Price Breaks ──────────────────────────────────────────────────
// Quantity-threshold pricing per supplier for this item (e.g. "Sucranna
// Sugar from Supplier X: 1–49kg = £0.68/kg, 50–199kg = £0.60/kg, 200kg+ =
// £0.52/kg"). Feeds into the PO-line suggested price, recipe costing, and
// auto-generated PO suggested prices — see priceBreakService.js — but this
// section is purely CRUD: add/edit/remove tiers.

// ── Approved Suppliers ──────────────────────────────────────────────────────
// Which suppliers this item can be bought from (migration 063). Costing and
// auto-generated POs don't just use whichever is "primary" here — they pick
// whichever approved supplier is cheapest at the relevant quantity (a price
// break or this row's own flat price). Primary is only the default shown
// when nothing else has decided a price yet, and what the legacy single
// "Preferred supplier" field on the item form stays in sync with.
function ApprovedSuppliersSection({ itemId, unit }) {
  const { data, loading, refetch } = useApi(`/stock/${itemId}/suppliers`, [itemId]);
  const suppliers = data?.suppliers ?? [];
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  async function handleMakePrimary(row) {
    setBusyId(row.id);
    try {
      await apiFetch(`/stock/${itemId}/suppliers/${row.id}/primary`, { method: 'PUT' });
      refetch();
    } catch (err) {
      alert(err.message);
    }
    setBusyId(null);
  }

  async function handleRemove(row) {
    if (!(await uiConfirm(`Remove ${row.supplier_name} as an approved supplier for this item?`))) return;
    setBusyId(row.id);
    try {
      await apiFetch(`/stock/${itemId}/suppliers/${row.id}`, { method: 'DELETE' });
      refetch();
    } catch (err) {
      alert(err.message);
    }
    setBusyId(null);
  }

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      {(adding || editing) && (
        <ApprovedSupplierModal
          itemId={itemId}
          unit={unit}
          existing={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); refetch(); }}
        />
      )}
      <SectionHeader
        label="Approved Suppliers"
        action={<Btn size="sm" variant="ghost" onClick={() => setAdding(true)}>+ Add Supplier</Btn>}
      />
      {loading ? (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Loading…</p>
      ) : !suppliers.length ? (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>
          No suppliers set up for this item yet. Add one to start pricing it — a second supplier here means costing and auto-generated POs automatically use whichever is cheaper.
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['Supplier', 'Flat price', 'Supplier SKU', 'Notes', ''].map(h => (
                <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Flat price' ? 'right' : 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {suppliers.map(row => (
              <tr key={row.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 10px', fontWeight: 600 }}>
                  {row.supplier_name}
                  {row.is_primary && (
                    <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: 'var(--pld-green)', border: '1px solid var(--pld-green)', borderRadius: 999, padding: '1px 6px', textTransform: 'uppercase', letterSpacing: '.03em' }}>Primary</span>
                  )}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{row.unit_price != null ? `£${fmt(row.unit_price, 4)}/${unit}` : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td style={{ padding: '6px 10px', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{row.supplier_sku || '—'}</td>
                <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }}>{row.notes || '—'}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {!row.is_primary && (
                    <button type="button" disabled={busyId === row.id} onClick={() => handleMakePrimary(row)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pld-green)', fontSize: 12, fontWeight: 600, marginRight: 10 }}>
                      Make primary
                    </button>
                  )}
                  <button type="button" onClick={() => setEditing(row)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pld-green)', fontSize: 12, fontWeight: 600, marginRight: 10 }}>Edit</button>
                  <button type="button" disabled={busyId === row.id} onClick={() => handleRemove(row)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 12, fontWeight: 600 }}>
                    {busyId === row.id ? '…' : 'Remove'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ApprovedSupplierModal({ itemId, unit, existing, onClose, onSaved }) {
  const { data: supplierData } = useApi('/suppliers');
  const suppliers = supplierData?.suppliers ?? [];
  const [form, setForm] = useState({
    supplier_id: existing?.supplier_id ?? '',
    unit_price: existing && existing.unit_price != null ? String(existing.unit_price) : '',
    supplier_sku: existing?.supplier_sku ?? '',
    notes: existing?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const canSubmit = (existing || form.supplier_id) && (form.unit_price === '' || parseFloat(form.unit_price) >= 0);

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const body = {
        unit_price: form.unit_price === '' ? null : parseFloat(form.unit_price),
        supplier_sku: form.supplier_sku || undefined,
        notes: form.notes || undefined,
      };
      if (!existing) body.supplier_id = form.supplier_id;
      await apiFetch(`/stock/${itemId}/suppliers${existing ? `/${existing.id}` : ''}`, {
        method: existing ? 'PUT' : 'POST',
        body,
      });
      onSaved();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title={existing ? 'Edit Approved Supplier' : 'Add Approved Supplier'} onClose={onClose} width={440}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Supplier *">
          <SelectEl value={form.supplier_id} disabled={!!existing} onChange={e => set('supplier_id', e.target.value)}>
            <option value="">— select supplier —</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </SelectEl>
        </Field>
        <Field label={`Flat price (£/${unit})`} hint="Optional — leave blank if this supplier only has quantity-break pricing set up below">
          <Input type="number" min="0" step="0.0001" value={form.unit_price} onChange={e => set('unit_price', e.target.value)} />
        </Field>
        <Field label="Supplier SKU" hint="Optional — their own code for this item">
          <Input value={form.supplier_sku} onChange={e => set('supplier_sku', e.target.value)} placeholder="Optional" />
        </Field>
        <Field label="Notes">
          <Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional" />
        </Field>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !canSubmit}>{saving ? 'Saving…' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function PriceBreaksSection({ itemId, unit }) {
  const { data, loading, refetch } = useApi(`/stock/${itemId}/price-breaks`, [itemId]);
  const breaks = data?.price_breaks ?? [];
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  async function handleDelete(brk) {
    setDeletingId(brk.id);
    try {
      await apiFetch(`/stock/${itemId}/price-breaks/${brk.id}`, { method: 'DELETE' });
      refetch();
    } catch (err) {
      alert(err.message);
    }
    setDeletingId(null);
  }

  // Group by supplier so tiers for the same supplier sit together, lowest
  // threshold first (the order the API already returns them in).
  const bySupplier = {};
  for (const b of breaks) {
    if (!bySupplier[b.supplier_id]) bySupplier[b.supplier_id] = { name: b.supplier_name, rows: [] };
    bySupplier[b.supplier_id].rows.push(b);
  }

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      {(adding || editing) && (
        <PriceBreakModal
          itemId={itemId}
          unit={unit}
          existing={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); refetch(); }}
        />
      )}
      <SectionHeader
        label="Supplier Price Breaks"
        action={<Btn size="sm" variant="ghost" onClick={() => setAdding(true)}>+ Add Price Break</Btn>}
      />
      {loading ? (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Loading…</p>
      ) : !breaks.length ? (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>
          No quantity-break pricing set up for this item yet. Add one when a supplier quotes a lower price at a certain order size.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {Object.entries(bySupplier).map(([supplierId, group]) => (
            <div key={supplierId}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{group.name}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['From qty', 'Unit price', 'Notes', ''].map(h => (
                      <th key={h} style={{ padding: '6px 10px', textAlign: h === 'From qty' || h === 'Unit price' ? 'right' : 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map(b => (
                    <tr key={b.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>{fmt(b.min_quantity)} {unit}+</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>£{fmt(b.unit_price, 4)}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }}>{b.notes || '—'}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button type="button" onClick={() => setEditing(b)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pld-green)', fontSize: 12, fontWeight: 600, marginRight: 10 }}>Edit</button>
                        <button type="button" disabled={deletingId === b.id} onClick={async () => { if ((await uiConfirm('Remove this price break?'))) handleDelete(b); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 12, fontWeight: 600 }}>
                          {deletingId === b.id ? 'Removing…' : 'Remove'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PriceBreakModal({ itemId, unit, existing, onClose, onSaved }) {
  const { data: supplierData } = useApi('/suppliers');
  const suppliers = supplierData?.suppliers ?? [];
  const [form, setForm] = useState({
    supplier_id: existing?.supplier_id ?? '',
    min_quantity: existing ? String(existing.min_quantity) : '',
    unit_price: existing ? String(existing.unit_price) : '',
    notes: existing?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const canSubmit = (existing || form.supplier_id) && form.min_quantity !== '' && form.unit_price !== ''
    && parseFloat(form.min_quantity) >= 0 && parseFloat(form.unit_price) >= 0;

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const body = {
        min_quantity: parseFloat(form.min_quantity),
        unit_price: parseFloat(form.unit_price),
        notes: form.notes || undefined,
      };
      if (!existing) body.supplier_id = form.supplier_id;
      await apiFetch(`/stock/${itemId}/price-breaks${existing ? `/${existing.id}` : ''}`, {
        method: existing ? 'PUT' : 'POST',
        body,
      });
      onSaved();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title={existing ? 'Edit Price Break' : 'Add Price Break'} onClose={onClose} width={440}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Supplier *">
          <SelectEl value={form.supplier_id} disabled={!!existing} onChange={e => set('supplier_id', e.target.value)}>
            <option value="">— select supplier —</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </SelectEl>
        </Field>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Field label={`From qty (${unit}) *`} hint="Order size this price kicks in at">
              <Input type="number" min="0" step="0.0001" value={form.min_quantity} onChange={e => set('min_quantity', e.target.value)} />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Unit price (£) *">
              <Input type="number" min="0" step="0.0001" value={form.unit_price} onChange={e => set('unit_price', e.target.value)} />
            </Field>
          </div>
        </div>
        <Field label="Notes" hint="Optional — e.g. where this quote came from">
          <Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional" />
        </Field>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !canSubmit}>{saving ? 'Saving…' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Stock List ────────────────────────────────────────────────────────────────
// search/filterCat are passed in from the parent (rather than kept as local
// state here) so they survive clicking into an item's detail view and back —
// StockList unmounts entirely while a detail view is open, which would
// otherwise wipe out whatever was typed in the search box.
function StockList({ onSelectItem, search, setSearch, filterCat, setFilterCat }) {
  const [showCreate, setShowCreate] = useState(false);

  const url = `/stock${filterCat ? `?category=${filterCat}` : ''}`;
  const { data, loading, error, refetch } = useApi(url, [filterCat]);

  const allItems = data?.stock_items ?? [];
  const items = search.trim()
    ? allItems.filter(i => i.name.toLowerCase().includes(search.toLowerCase()) || i.sku.toLowerCase().includes(search.toLowerCase()))
    : allItems;

  const TAB_STYLE = (active) => ({
    padding: '6px 14px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
    background: active ? 'var(--pld-green)' : 'var(--surface-2)',
    color: active ? '#fff' : 'var(--text)',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {showCreate && (
        <CreateItemModal
          onClose={() => setShowCreate(false)}
          onCreated={(item) => { setShowCreate(false); refetch(); onSelectItem(item.id); }}
        />
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name or SKU…"
          style={{ padding: '8px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', color: 'var(--text)', width: 240 }}
        />
        <Btn onClick={() => setShowCreate(true)}>+ New Item</Btn>
      </div>

      {/* Category tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button style={TAB_STYLE(filterCat === '')} onClick={() => setFilterCat('')}>All</button>
        {CATEGORIES.map(c => (
          <button key={c.value} style={TAB_STYLE(filterCat === c.value)} onClick={() => setFilterCat(c.value)}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#dc2626' }}>Failed to load stock items.</div>
        ) : !items.length ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
            {search ? `No items matching "${search}"` : 'No stock items yet — create one to get started.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                  {['SKU', 'Name', 'Category', 'Unit', 'ABV', 'Flags', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr
                    key={item.id}
                    onClick={() => onSelectItem(item.id)}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{item.sku}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text)' }}>
                      {item.name}
                      {!item.is_active && <span style={{ marginLeft: 6, fontSize: 10, color: '#6b7280', background: '#f3f4f6', padding: '1px 5px', borderRadius: 4, fontWeight: 600 }}>INACTIVE</span>}
                    </td>
                    <td style={{ padding: '10px 14px' }}><CategoryBadge category={item.category} /></td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{item.unit}</td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: item.abv == null && item.is_alcoholic ? '#b45309' : undefined }} title={item.abv == null && item.is_alcoholic ? 'Alcoholic but no ABV set — duty and W1 litres can\'t be worked out' : undefined}>
                      {item.abv != null ? `${fmtAbv(item.abv)}%` : item.is_alcoholic ? 'not set' : '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {item.is_alcoholic && <Pill on>Alcoholic</Pill>}
                        {item.is_bonded && <Pill on>Bonded</Pill>}
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-muted)', fontSize: 18 }}>›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-2)' }}>
            {items.length} item{items.length !== 1 ? 's' : ''}
            {search && ` matching "${search}"`}
          </div>
        )}
      </div>
    </div>
  );
}

// ── PO status config ──────────────────────────────────────────────────────────
const PO_STATUS = {
  draft:     { bg: '#f3f4f6', text: '#374151', label: 'Draft' },
  sent:      { bg: '#dbeafe', text: '#1e40af', label: 'Sent' },
  confirmed: { bg: '#fef9c3', text: '#854d0e', label: 'Confirmed' },
  partial:   { bg: '#ffedd5', text: '#9a3412', label: 'Part Received' },
  received:  { bg: '#dcfce7', text: '#166534', label: 'Received' },
  cancelled: { bg: '#fee2e2', text: '#991b1b', label: 'Cancelled' },
};

function POBadge({ status }) {
  const s = PO_STATUS[status] ?? { bg: '#f3f4f6', text: '#374151', label: status };
  return <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: s.bg, color: s.text }}>{s.label}</span>;
}

// ── Supplier Form Modal ───────────────────────────────────────────────────────
function SupplierFormModal({ initial, onClose, onSaved, onUseExisting }) {
  const blank = { name: '', contact_name: '', email: '', phone: '', address: '', payment_terms: '', notes: '', vat_number: '', website: '', materials_supplied: '', supplier_type: 'goods', minimum_order_value: '', default_delivery_charge: '' };
  const [form, setForm] = useState(initial ?? blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // New supplier: fill the form from something they've sent (quote,
  // invoice, pro-forma, letterhead) or a pasted email signature.
  const [docFile, setDocFile] = useState(null);
  const [docText, setDocText] = useState('');
  const [reading, setReading] = useState(false);
  const [readNote, setReadNote] = useState('');
  const [existing, setExisting] = useState(null);
  async function readDocument() {
    setReading(true); setError(''); setReadNote(''); setExisting(null);
    try {
      const fd = new FormData();
      if (docFile) fd.append('file', docFile);
      if (docText.trim()) fd.append('text', docText);
      const res = await fetch('/api/suppliers/read-document', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Couldn't read it (HTTP ${res.status})`);
      const d = body.details || {};
      const filled = Object.keys(blank).filter(k => d[k] !== '' && d[k] != null);
      setForm(f => {
        const next = { ...f };
        for (const k of filled) next[k] = d[k];
        return next;
      });
      setExisting(body.existing || null);
      setReadNote(filled.length ? `Filled ${filled.length} field${filled.length === 1 ? '' : 's'} from the document — check them before saving.` : 'Nothing usable found in that document.');
    } catch (err) { setError(err.message); } finally { setReading(false); }
  }

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const body = {
        ...form,
        minimum_order_value: form.minimum_order_value !== '' && form.minimum_order_value != null ? parseFloat(form.minimum_order_value) : null,
        default_delivery_charge: form.default_delivery_charge !== '' && form.default_delivery_charge != null ? parseFloat(form.default_delivery_charge) : null,
      };
      const res = initial?.id
        ? await apiFetch(`/suppliers/${initial.id}`, { method: 'PUT', body })
        : await apiFetch('/suppliers', { method: 'POST', body });
      onSaved(res.supplier);
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title={initial?.id ? 'Edit Supplier' : 'New Supplier'} onClose={onClose} width={520}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!initial?.id && (
          <div style={{ background: 'var(--surface-2)', border: '1px dashed var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Fill from a document</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Drop in a quote, invoice, pro-forma or anything on their letterhead — or paste their email signature — and Blake fills in the details below.</div>
            <input type="file" accept="application/pdf,image/*" onChange={e => setDocFile(e.target.files?.[0] ?? null)} />
            <textarea value={docText} onChange={e => setDocText(e.target.value)} rows={2} placeholder="…or paste an email signature / their details here"
              style={{ padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)', resize: 'vertical', fontFamily: 'inherit' }} />
            <div>
              <Btn type="button" size="sm" onClick={readDocument} disabled={reading || (!docFile && !docText.trim())}>{reading ? 'Reading…' : 'Read details'}</Btn>
            </div>
            {readNote && <div style={{ fontSize: 12.5, color: 'var(--pld-green)' }}>{readNote}</div>}
            {existing && (
              <div style={{ fontSize: 12.5, background: '#fef3c7', color: '#92400e', borderRadius: 'var(--radius)', padding: '8px 10px' }}>
                Looks like <strong>{existing.name}</strong>{existing.is_active === false ? ' (inactive)' : ''} is already in Blake (same {existing.matched_by}).
                {onUseExisting && existing.is_active !== false && <> <button type="button" onClick={() => onUseExisting(existing)} style={{ background: 'none', border: 'none', padding: 0, color: '#92400e', textDecoration: 'underline', cursor: 'pointer', fontSize: 12.5 }}>Open it instead</button></>}
              </div>
            )}
          </div>
        )}
        <Field label="Name *"><Input value={form.name} onChange={e => set('name', e.target.value)} required autoFocus /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Contact Name"><Input value={form.contact_name ?? ''} onChange={e => set('contact_name', e.target.value)} /></Field>
          <Field label="Payment Terms"><Input value={form.payment_terms ?? ''} onChange={e => set('payment_terms', e.target.value)} placeholder="e.g. 30 days" /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Email"><Input type="email" value={form.email ?? ''} onChange={e => set('email', e.target.value)} /></Field>
          <Field label="Phone"><Input value={form.phone ?? ''} onChange={e => set('phone', e.target.value)} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <Field label="Address"><Input value={form.address ?? ''} onChange={e => set('address', e.target.value)} /></Field>
          <Field label="VAT Number"><Input value={form.vat_number ?? ''} onChange={e => set('vat_number', e.target.value)} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <Field label="Website"><Input value={form.website ?? ''} onChange={e => set('website', e.target.value)} placeholder="https://…" /></Field>
          <Field label="Type">
            <SelectEl value={form.supplier_type ?? 'goods'} onChange={e => set('supplier_type', e.target.value)}>
              <option value="goods">Goods</option>
              <option value="service">Service</option>
            </SelectEl>
          </Field>
        </div>
        <Field label="Materials / Services Supplied"><Input value={form.materials_supplied ?? ''} onChange={e => set('materials_supplied', e.target.value)} placeholder="e.g. Apples, or Pest Control" /></Field>
        <Field label="Minimum Order Value (£)" hint="If this supplier won't process an order below a certain spend, Generate POs will flag a draft order that falls short">
          <Input type="number" min="0" step="0.01" value={form.minimum_order_value ?? ''} onChange={e => set('minimum_order_value', e.target.value)} placeholder="e.g. 150.00" />
        </Field>
        <Field label="Standard Delivery Charge (£)" hint="Added automatically as a line on every PO to this supplier — manual or auto-generated. Leave blank if it varies or there isn't one.">
          <Input type="number" min="0" step="0.01" value={form.default_delivery_charge ?? ''} onChange={e => set('default_delivery_charge', e.target.value)} placeholder="e.g. 25.80" />
        </Field>
        <Field label="Notes"><Input value={form.notes ?? ''} onChange={e => set('notes', e.target.value)} placeholder="Optional notes…" /></Field>
        {initial?.id && <SupplierAccountForms supplierId={initial.id} />}
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !form.name.trim()}>{saving ? 'Saving…' : (initial?.id ? 'Save Changes' : 'Create Supplier')}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Supplier account form ─────────────────────────────────────────────────────
// A new supplier's account / credit application: Blake reads it, sets the
// supplier up and fills our side in, ready to sign and send back.
async function downloadFile(url, fallbackName) {
  const res = await fetch(`/api${url}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Download failed'); }
  const blob = await res.blob();
  const m = (res.headers.get('content-disposition') || '').match(/filename="?([^";]+)"?/i);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

const SUP_FIELDS = [['name', 'Name *'], ['contact_name', 'Contact'], ['email', 'Email'], ['phone', 'Phone'], ['address', 'Address', 2], ['vat_number', 'VAT number'], ['payment_terms', 'Payment terms'], ['materials_supplied', 'Supplies', 2]];

function AccountFormModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [read, setRead] = useState(null);
  const [sup, setSup] = useState(null);
  const [useExisting, setUseExisting] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(null);

  async function readForm() {
    setBusy('read'); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch('/api/suppliers/account-forms/read', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Couldn't read it (HTTP ${res.status})`);
      setRead(body); setSup(body.supplier);
      setUseExisting(!!body.existing && body.existing.is_active !== false);
      setAnswers(body.answers.map(a => ({ ...a, value: a.value ?? '', original: a.value ?? '', remember: false })));
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  }
  const setAnswer = (i, value) => setAnswers(list => list.map((a, j) => j === i ? { ...a, value, remember: value.trim() !== '' && value !== a.original } : a));
  const toggleRemember = (i) => setAnswers(list => list.map((a, j) => j === i ? { ...a, remember: !a.remember } : a));

  async function fill() {
    setBusy('fill'); setError('');
    try {
      const body = {
        ...(useExisting ? { supplier_id: read.existing.id } : { supplier: sup }),
        answers: answers.map(({ id, question, value, placement }) => ({ id, question, value: value.trim() || null, placement })),
        remember: answers.filter(a => a.remember && a.value.trim()).map(a => ({ question: a.question, value: a.value.trim() })),
      };
      const r = await apiFetch(`/suppliers/account-forms/${read.id}/fill`, { method: 'POST', body });
      setDone(r);
      await downloadFile(`/suppliers/account-forms/${read.id}/file?filled=1`, r.form.file_name).catch(e => setError(e.message));
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  }

  const muted = { fontSize: 12.5, color: 'var(--text-muted)', margin: 0 };
  if (done) {
    return (
      <Modal title="Account form filled" onClose={() => onDone(done.supplier, !useExisting)} width={480}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14 }}><strong>{done.supplier.name}</strong> {useExisting ? 'has the form on file' : 'is set up as a supplier'}, and the filled form has downloaded. Check it, sign it and send it back to them.</p>
          {read.notes && <p style={{ ...muted, background: '#fef3c7', color: '#92400e', padding: '8px 10px', borderRadius: 'var(--radius)' }}>{read.notes}</p>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn variant="ghost" onClick={() => downloadFile(`/suppliers/account-forms/${read.id}/file?filled=1`, done.form.file_name).catch(e => setError(e.message))}>Download again</Btn>
            <Btn onClick={() => onDone(done.supplier, !useExisting)}>{useExisting ? 'Done' : 'Next: SALSA approval'}</Btn>
          </div>
          {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        </div>
      </Modal>
    );
  }

  if (!read) {
    return (
      <Modal title="Supplier account form" onClose={onClose} width={480}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={muted}>Drop in a new supplier's account or credit application form (PDF or Word). Blake adds the supplier and fills in our details, ready for you to sign and send back.</p>
          <input type="file" accept="application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={e => setFile(e.target.files?.[0] ?? null)} />
          {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={readForm} disabled={!file || busy}>{busy ? 'Reading the form…' : 'Read form'}</Btn>
          </div>
        </div>
      </Modal>
    );
  }

  const blanks = answers.filter(a => !a.value.trim()).length;
  const cell = { padding: '6px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };
  return (
    <Modal title={`Account form — ${read.file_name}`} onClose={onClose} width={760}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>The supplier</div>
        {read.existing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13.5 }}>
            <label><input type="radio" checked={useExisting} disabled={read.existing.is_active === false} onChange={() => setUseExisting(true)} /> Use <strong>{read.existing.name}</strong>{read.existing.is_active === false ? ' (inactive)' : ''} — already in Blake (same {read.existing.matched_by})</label>
            <label><input type="radio" checked={!useExisting} onChange={() => setUseExisting(false)} /> Add as a new supplier</label>
          </div>
        )}
        {!useExisting && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {SUP_FIELDS.map(([k, label, span]) => (
              <div key={k} style={span ? { gridColumn: 'span 2' } : undefined}>
                <Field label={label}><Input value={sup[k] ?? ''} onChange={e => setSup(s => ({ ...s, [k]: e.target.value }))} /></Field>
              </div>
            ))}
          </div>
        )}

        <div style={{ fontSize: 13, fontWeight: 600 }}>Our answers on the form</div>
        {read.scanned && <p style={{ ...muted, color: '#92400e' }}>This looks like a scanned form, so Blake can't write on it. It can still add the supplier.</p>}
        {read.notes && <p style={{ ...muted, background: '#fef3c7', color: '#92400e', padding: '8px 10px', borderRadius: 'var(--radius)' }}>{read.notes}</p>}
        {answers.length > 0 && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', maxHeight: 340, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  {['Question', 'Answer', 'Remember'].map(h => <th key={h} style={{ ...cell, textAlign: 'left', fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {answers.map((a, i) => (
                  <tr key={a.id}>
                    <td style={{ ...cell, width: '36%' }}>{a.question}</td>
                    <td style={cell}>
                      <input value={a.value} onChange={e => setAnswer(i, e.target.value)} placeholder="Blank — fill in, or leave to write by hand"
                        style={{ width: '100%', padding: '5px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 13, color: 'var(--text)', background: a.value.trim() ? 'var(--surface)' : '#fef3c7', boxSizing: 'border-box' }} />
                    </td>
                    <td style={{ ...cell, textAlign: 'center', width: 70 }}>
                      <input type="checkbox" checked={a.remember} onChange={() => toggleRemember(i)} title="Use this answer on future forms" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p style={muted}>{blanks ? `${blanks} left blank (yellow). ` : ''}Tick "Remember" and Blake uses that answer on future forms. Signatures are left for you.</p>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={fill} disabled={!!busy || (!useExisting && !String(sup?.name || '').trim())}>{busy ? 'Filling…' : useExisting ? 'Fill form' : 'Add supplier & fill form'}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// The account forms filled for a supplier, on its Edit screen.
function SupplierAccountForms({ supplierId }) {
  const { data } = useApi(`/suppliers/${supplierId}/account-forms`);
  const forms = data?.forms ?? [];
  const [err, setErr] = useState('');
  if (!forms.length) return null;
  return (
    <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontWeight: 600 }}>Account forms</div>
      {forms.map(f => (
        <div key={f.id} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{f.file_name} <span style={{ color: 'var(--text-muted)' }}>· {new Date(f.filled_at).toLocaleDateString('en-GB')}</span></span>
          <button type="button" onClick={() => downloadFile(`/suppliers/account-forms/${f.id}/file?filled=1`, f.file_name).catch(e => setErr(e.message))} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pld-green)', cursor: 'pointer', fontSize: 13 }}>Filled</button>
          <button type="button" onClick={() => downloadFile(`/suppliers/account-forms/${f.id}/file`, f.file_name).catch(e => setErr(e.message))} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13 }}>Original</button>
        </div>
      ))}
      {err && <span style={{ color: '#dc2626' }}>{err}</span>}
    </div>
  );
}

// ── Suppliers Tab ─────────────────────────────────────────────────────────────
// After a new supplier is saved: set up their SALSA supplier approval in the
// same go — read a certificate, fill the approval in, or leave it for later.
function SupplierSalsaStep({ supplier, onClose }) {
  const [mode, setMode] = useState('choose'); // choose | cert | approval
  const [prefill, setPrefill] = useState({ supplier_id: supplier.id });
  const [certId, setCertId] = useState(null);
  if (mode === 'cert') {
    return (
      <UploadCertificateModal
        defaultSupplierId={supplier.id}
        onClose={() => setMode('choose')}
        onParsed={({ supplierId, certificateId, fileName, parsed }) => {
          setPrefill({
            supplier_id: supplierId,
            cert_type: parsed.cert_type,
            expiry_date: parsed.expiry_date,
            cert_document_url: `/salsa/suppliers/${supplierId}/certificates/${certificateId}/file`,
            audit_note: buildCertAuditNote(parsed, fileName),
            flags: parsed.name_matches_supplier === false
              ? [...(parsed.flags || []), `Certificate holder name may not match this supplier: "${parsed.certificate_holder_name || 'unknown'}"`]
              : (parsed.flags || []),
          });
          setCertId(certificateId);
          setMode('approval');
        }}
      />
    );
  }
  if (mode === 'approval') {
    return <SupplierApprovalModal prefill={prefill} certificateUploadId={certId} onClose={onClose} onSaved={onClose} />;
  }
  return (
    <Modal title={`${supplier.name} saved`} onClose={onClose} width={460}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 14 }}>Next, their <strong>SALSA supplier approval</strong>: approval status, risk level, review date and their certificate.</p>
        <Btn onClick={() => setMode('cert')}>Upload their certificate — Blake reads it</Btn>
        <Btn variant="ghost" onClick={() => setMode('approval')}>Fill in the approval without a certificate</Btn>
        <Btn variant="ghost" onClick={onClose}>Skip for now</Btn>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>You can also do this later under SALSA → Supplier Approvals.</p>
      </div>
    </Modal>
  );
}

function SuppliersTab() {
  const { data, loading, refetch } = useApi('/suppliers');
  const suppliers = data?.suppliers ?? [];
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [salsaFor, setSalsaFor] = useState(null);
  const [accountForm, setAccountForm] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showCreate && <SupplierFormModal onClose={() => setShowCreate(false)}
        onSaved={(s) => { setShowCreate(false); refetch(); if (s?.id) setSalsaFor(s); }}
        onUseExisting={(s) => { setShowCreate(false); setEditing(suppliers.find(x => x.id === s.id) || s); }} />}
      {editing && <SupplierFormModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}
      {salsaFor && <SupplierSalsaStep supplier={salsaFor} onClose={() => setSalsaFor(null)} />}
      {accountForm && <AccountFormModal onClose={() => setAccountForm(false)}
        onDone={(s, isNew) => { setAccountForm(false); refetch(); if (isNew && s?.id) setSalsaFor(s); }} />}

      <SectionHeader label="Suppliers" action={<div style={{ display: 'flex', gap: 8 }}>
        <Btn size="sm" variant="ghost" onClick={() => setAccountForm(true)}>Account form</Btn>
        <Btn size="sm" onClick={() => setShowCreate(true)}>+ New Supplier</Btn>
      </div>} />

      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
      : !suppliers.length ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No suppliers yet.</p>
      : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                {['Supplier', 'Contact', 'Email', 'Phone', 'Payment Terms', 'Total Order Value', ''].map(h => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s, i) => (
                <tr key={s.id} style={{ borderBottom: i < suppliers.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                    {s.name}
                    {s.supplier_type === 'service' && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', background: 'var(--surface-2)', padding: '1px 6px', borderRadius: 99 }}>Service</span>}
                    {s.materials_supplied && <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400, marginTop: 2 }}>{s.materials_supplied}</div>}
                    {s.notes && <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400, marginTop: 2 }}>{s.notes}</div>}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{s.contact_name || '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{s.email || '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{s.phone || '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{s.payment_terms || '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>
                    £{Number(s.total_order_value ?? 0).toFixed(2)}
                    {s.order_count > 0 && <span style={{ fontSize: 12 }}> ({s.order_count} order{s.order_count === 1 ? '' : 's'})</span>}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <Btn size="sm" variant="ghost" onClick={() => setEditing(s)}>Edit</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Create PO Modal ───────────────────────────────────────────────────────────
// ── New PO from a supplier quote ─────────────────────────────────────────────
// Drop in the quote; Blake reads it (supplier, items, prices), matches it to
// suppliers and stock items, and shows a draft to check. Nothing is created
// until "Create draft PO". The supplier's own codes are remembered so their
// next quote matches by code.
function QuoteItemPicker({ line, onPick }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (!searching || !search.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { stock_items } = await apiFetch(`/stock?search=${encodeURIComponent(search)}`); setResults(stock_items || []); } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [search, searching]);
  if (searching) {
    return (
      <div>
        <Input autoFocus placeholder="Search stock items…" value={search} onChange={e => setSearch(e.target.value)} />
        {results.length > 0 && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginTop: 4, maxHeight: 150, overflowY: 'auto', background: 'var(--surface)' }}>
            {results.map(i => (
              <div key={i.id} onClick={() => { onPick(i); setSearching(false); setSearch(''); }}
                style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                {i.name} <span style={{ color: 'var(--text-muted)' }}>({i.sku}, {i.unit})</span>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={() => setSearching(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', padding: '4px 0 0' }}>cancel</button>
      </div>
    );
  }
  return (
    <SelectEl value={line.stock_item_id || ''} onChange={e => {
      if (e.target.value === '__search') { setSearching(true); return; }
      onPick(line.candidates.find(c => c.id === e.target.value) || null);
    }}>
      <option value="">— match to a stock item —</option>
      {line.candidates.map(c => <option key={c.id} value={c.id}>{c.name} ({c.sku}, {c.unit})</option>)}
      <option value="__search">Search for another item…</option>
    </SelectEl>
  );
}

function QuotePOModal({ onClose, onCreated }) {
  const { data: suppData } = useApi('/suppliers');
  const suppliers = suppData?.suppliers ?? [];
  const [reading, setReading] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [supplierId, setSupplierId] = useState('');
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function read(file) {
    if (!file) return;
    setReading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/purchase-orders/from-quote', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Upload failed (HTTP ${res.status})`);
      const p = body.proposal;
      setProposal(p);
      setSupplierId(p.supplier_id || '');
      setLines(p.lines.map((l, i) => ({ ...l, key: i, include: true, item_unit: (l.candidates.find(c => c.id === l.stock_item_id) || {}).unit || '', vat_rate: l.vat_rate ?? p.vat_rate ?? '' })));
    } catch (e) { setError(e.message); }
    finally { setReading(false); }
  }

  const upd = (key, patch) => setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));
  const chosen = lines.filter(l => l.include);
  const total = chosen.reduce((s, l) => s + (Number(l.po_quantity) || 0) * (Number(l.po_unit_price) || 0), 0);
  const unmatched = chosen.filter(l => !l.stock_item_id).length;

  async function create() {
    setSaving(true); setError('');
    try {
      const noteBits = [proposal.quote_ref && `From quote ${proposal.quote_ref}`, proposal.valid_until && `valid until ${proposal.valid_until}`, proposal.notes].filter(Boolean);
      const res = await apiFetch('/purchase-orders', {
        method: 'POST',
        body: {
          supplier_id: supplierId,
          notes: noteBits.join(' · ') || undefined,
          remember_supplier_codes: true,
          lines: chosen.map(l => ({
            stock_item_id: l.stock_item_id,
            quantity_ordered: Number(l.po_quantity),
            unit_price: l.po_unit_price !== '' && l.po_unit_price != null ? Number(l.po_unit_price) : null,
            supplier_reference: l.supplier_code || null,
            vat_rate: l.vat_rate === '' || l.vat_rate == null ? null : Number(l.vat_rate),
          })),
        },
      });
      onCreated(res.purchase_order);
    } catch (e) { setError(e.message); setSaving(false); }
  }

  return (
    <Modal title="New PO from a supplier quote" onClose={onClose} width={820}>
      {!proposal ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
            Choose the supplier's quote (PDF or a photo). Blake reads the supplier, items and prices and shows you a draft to check. Nothing is created until you confirm.
          </p>
          <input type="file" accept="application/pdf,image/*" disabled={reading} onChange={e => read(e.target.files?.[0])} />
          {reading && <p style={{ margin: 0, fontSize: 13 }}>Reading the quote…</p>}
          {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Supplier *" hint={proposal.supplier_matched_by ? `Matched by ${proposal.supplier_matched_by}` : `Quote is from "${proposal.supplier?.name || '?'}" — pick the supplier (add them under Suppliers first if they're new)`}>
              <SelectEl value={supplierId} onChange={e => setSupplierId(e.target.value)} required>
                <option value="">— select supplier —</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </SelectEl>
            </Field>
            <Field label="Quote">
              <div style={{ fontSize: 13, paddingTop: 8 }}>
                {proposal.quote_ref || '—'}{proposal.quote_date ? ` · ${proposal.quote_date}` : ''}{proposal.valid_until ? ` · valid until ${proposal.valid_until}` : ''}
              </div>
            </Field>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  {['', 'On the quote', 'Blake stock item', 'Qty', 'Unit price £', 'VAT'].map(h => <th key={h} style={{ textAlign: 'left', padding: '7px 8px', fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.key} style={{ borderTop: '1px solid var(--border)', verticalAlign: 'top', opacity: l.include ? 1 : 0.45 }}>
                    <td style={{ padding: 8 }}><input type="checkbox" checked={l.include} onChange={e => upd(l.key, { include: e.target.checked })} /></td>
                    <td style={{ padding: 8 }}>
                      <div style={{ fontWeight: 600 }}>{l.description}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{l.supplier_code || 'no code'} · {l.quantity} {l.unit} @ £{l.unit_price}</div>
                      {l.conversion_note && <div style={{ fontSize: 11.5, color: '#92400e', marginTop: 2 }}>{l.conversion_note}</div>}
                    </td>
                    <td style={{ padding: 8, minWidth: 230 }}>
                      <QuoteItemPicker line={l} onPick={item => upd(l.key, item ? { stock_item_id: item.id, item_unit: item.unit, matched_by: 'you', candidates: l.candidates.some(c => c.id === item.id) ? l.candidates : [{ id: item.id, sku: item.sku, name: item.name, unit: item.unit }, ...l.candidates] } : { stock_item_id: null })} />
                      {l.matched_by && l.stock_item_id && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>matched by {l.matched_by}</div>}
                    </td>
                    <td style={{ padding: 8, width: 110 }}>
                      <Input type="number" min="0" step="0.001" value={l.po_quantity ?? ''} onChange={e => upd(l.key, { po_quantity: e.target.value })} />
                      {l.item_unit && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{l.item_unit}</div>}
                    </td>
                    <td style={{ padding: 8, width: 110 }}>
                      <Input type="number" min="0" step="0.0001" value={l.po_unit_price ?? ''} onChange={e => upd(l.key, { po_unit_price: e.target.value })} />
                      {l.item_unit && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>per {l.item_unit}</div>}
                    </td>
                    <td style={{ padding: 8, width: 104 }}>
                      <VatSelect value={l.vat_rate} onChange={v => upd(l.key, { vat_rate: v })} blankLabel="Default" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)' }}>{proposal.delivery_charge ? `Quote shows a delivery charge of £${proposal.delivery_charge}. ` : ''}{proposal.notes || ''}</span>
            <strong>Net £{total.toFixed(2)}</strong>
          </div>

          {unmatched > 0 && <p style={{ margin: 0, fontSize: 13, color: '#b45309' }}>{unmatched} line{unmatched === 1 ? '' : 's'} not matched to a stock item: match {unmatched === 1 ? 'it' : 'them'} or untick to leave out. If it's a new item, add it on the Stock page first.</p>}
          {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={create} disabled={saving || !supplierId || !chosen.length || unmatched > 0 || chosen.some(l => !(Number(l.po_quantity) > 0))}>
              {saving ? 'Creating…' : 'Create draft PO →'}
            </Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

function CreatePOModal({ onClose, onCreated }) {
  const { data: suppData } = useApi('/suppliers');
  const suppliers = suppData?.suppliers ?? [];
  const [form, setForm] = useState({ supplier_id: '', order_date: '', expected_delivery_date: '', notes: '', vat_rate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const res = await apiFetch('/purchase-orders', {
        method: 'POST',
        body: { ...form, vat_rate: form.vat_rate !== '' ? parseFloat(form.vat_rate) : undefined },
      });
      onCreated(res.purchase_order);
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title="New Purchase Order" onClose={onClose} width={500}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Supplier *">
          <SelectEl value={form.supplier_id} onChange={e => set('supplier_id', e.target.value)} required>
            <option value="">— select supplier —</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </SelectEl>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Order Date"><Input type="date" value={form.order_date} onChange={e => set('order_date', e.target.value)} /></Field>
          <Field label="Expected Delivery"><Input type="date" value={form.expected_delivery_date} onChange={e => set('expected_delivery_date', e.target.value)} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Notes"><Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes…" /></Field>
          <Field label="VAT rate" hint="For lines without their own rate"><VatSelect value={form.vat_rate} onChange={v => set('vat_rate', v)} blankLabel="Company default" /></Field>
        </div>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !form.supplier_id}>{saving ? 'Creating…' : 'Create PO →'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// Every receipt mints its own batch_ref automatically (stockBatchService
// .createBatch) whether or not a supplier lot number was entered — that's
// "the" batch number. Labels are no longer printed automatically: after a
// receipt, ReceivedLabelsModal lists the new batch(es) with Print / Preview
// buttons, so nothing prints unless someone asks for it.
async function previewBatchLabel(batchId) {
  const token = localStorage.getItem('pld_token');
  const labelRes = await fetch(`/api/stock/batches/${batchId}/label.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!labelRes.ok) throw new Error('Failed to load the batch label');
  const blob = await labelRes.blob();
  const w = window.open(URL.createObjectURL(blob), '_blank');
  if (w) setTimeout(() => { try { w.print(); } catch (_) {} }, 400);
}

// Batches of a stock item: every goods receipt and production output is its
// own batch, with its own ABV / LPA, drawn down oldest first.
function BatchesSection({ itemId, item }) {
  const [showAll, setShowAll] = useState(false);
  const { data, loading } = useApi(`/stock/${itemId}/batches${showAll ? '?all=1' : ''}`, [itemId, showAll]);
  const batches = data?.data ?? data ?? [];
  const alcoholic = item.is_alcoholic;
  const abvs = [...new Set(batches.filter(b => b.status === 'active' && b.abv != null).map(b => b.abv))];
  const th = { padding: '6px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', verticalAlign: 'top' };
  const ORIGIN = { purchase: 'Received', production: 'Produced', opening: 'Opening stock' };

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <SectionHeader
        label={`Batches${batches.length ? ` (${batches.length})` : ''}`}
        action={<label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', color: 'var(--text-muted)' }}><input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} /> Include used-up</label>}
      />
      {alcoholic && abvs.length > 1 && (
        <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--text-muted)' }}>
          Batches in stock are at {abvs.length} different strengths ({abvs.sort((a, b) => a - b).map(a => `${fmtAbv(a)}%`).join(', ')}). Stock going out is counted at the strength of the batch it's drawn from, oldest first.
        </p>
      )}
      {loading && !data ? <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Loading…</p> : !batches.length ? (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>{showAll ? 'No batches recorded for this item.' : 'No batches in stock. Batches are created when stock is received or produced.'}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Batch', 'From', 'Date', 'Where', 'Left', ...(alcoholic ? ['ABV', 'LPA left'] : []), ''].map(h => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {batches.map(b => (
                <tr key={b.id} style={{ borderBottom: '1px solid var(--border)', opacity: b.status === 'active' ? 1 : 0.55 }}>
                  <td style={td}>
                    <code style={{ fontSize: 12 }}>{b.batch_ref}</code>
                    {b.supplier_lot_number && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Supplier lot {b.supplier_lot_number}</div>}
                    {b.status !== 'active' && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{b.status === 'depleted' ? 'Used up' : 'Written off'}</div>}
                  </td>
                  <td style={td}>
                    {ORIGIN[b.origin_type] || b.origin_type}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{b.origin_type === 'purchase' ? [b.supplier_name, b.po_ref].filter(Boolean).join(' · ') : b.run_ref || ''}</div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{new Date(b.batch_date).toLocaleDateString('en-GB')}</td>
                  <td style={{ ...td, fontSize: 12 }}>
                    {b.locations.length ? b.locations.map((l, i) => (
                      <div key={i}>{fmt(l.quantity)} × <code>{l.location_code}</code></div>
                    )) : '—'}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmt(b.quantity_remaining)} <span style={{ color: 'var(--text-muted)' }}>of {fmt(b.quantity_original)} {item.unit}</span></td>
                  {alcoholic && <td style={{ ...td, whiteSpace: 'nowrap', color: b.abv == null ? '#b45309' : undefined }}>{b.abv != null ? `${fmtAbv(b.abv)}%` : item.abv != null ? <span title="No ABV recorded on this batch — the item's ABV is used">{fmtAbv(item.abv)}%*</span> : 'unknown'}</td>}
                  {alcoholic && <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{b.lpa_remaining != null ? fmt(b.lpa_remaining, 3) : '—'}</td>}
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Btn size="sm" variant="ghost" onClick={() => previewBatchLabel(b.id).catch(e => alert(e.message))}>Label</Btn>{' '}
                    <PrintButton docType="batch_label" path={`/stock/batches/${b.id}/label.pdf`} title={`Batch label ${b.batch_ref}`} label="Print" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {alcoholic && batches.some(b => b.abv == null) && item.abv != null && (
            <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>* No ABV was recorded on this batch, so the item's ABV is used for it.</p>
          )}
        </div>
      )}
    </div>
  );
}

// Shown after receiving: the new batch number(s), each with Print (straight
// to the label printer, once set in Settings → Printers) and Preview.
function ReceivedLabelsModal({ batches, onClose }) {
  const { canPrint } = usePrintStatus();
  const [allState, setAllState] = useState('');
  const printable = batches.filter(b => b.id);

  async function printAll() {
    setAllState('Printing…');
    try {
      for (const b of printable) {
        await sendToPrinter({ docType: 'batch_label', path: `/stock/batches/${b.id}/label.pdf`, title: `Batch label ${b.ref || ''}`.trim() });
      }
      setAllState(`✓ Printed ${printable.length} label${printable.length === 1 ? '' : 's'}`);
    } catch (err) { setAllState(err.message); }
  }

  return (
    <Modal title="Stock received" onClose={onClose} width={560}>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-muted)' }}>
        {printable.length === 1 ? 'Print a batch label for it now if you need one.' : 'Print batch labels for them now if you need them.'}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {printable.map(b => (
          <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{b.item || 'Received stock'}</div>
              {b.ref && <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{b.ref}</div>}
            </div>
            <Btn size="sm" variant="ghost" onClick={() => previewBatchLabel(b.id).catch(e => alert(e.message))}>Preview</Btn>
            <PrintButton docType="batch_label" path={`/stock/batches/${b.id}/label.pdf`} title={`Batch label ${b.ref || ''}`.trim()} label="Print label" />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
        {allState && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{allState}</span>}
        {printable.length > 1 && canPrint('batch_label') && (
          <Btn variant="ghost" onClick={printAll} disabled={allState === 'Printing…'}>🖨 Print all {printable.length}</Btn>
        )}
        <Btn onClick={onClose}>Done</Btn>
      </div>
    </Modal>
  );
}

// ── Receive Line Modal ────────────────────────────────────────────────────────
function ReceiveLineModal({ poId, line, onClose, onReceived }) {
  const { data: locData } = useApi('/warehouse/locations');
  const locations = locData?.locations ?? [];
  const [form, setForm] = useState({
    quantity_received: String(line.quantity_ordered - (line.quantity_received ?? 0)),
    location_id: line.receive_location_id ?? '',
    lot_number: line.lot_number ?? '',
    intake_type: line.is_bonded ? 'INTAKE_UK' : 'INITIAL_INTAKE',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [received, setReceived] = useState(null); // batches to offer labels for
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const remaining = parseFloat(line.quantity_ordered) - parseFloat(line.quantity_received ?? 0);

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const res = await apiFetch(`/purchase-orders/${poId}/lines/${line.id}/receive`, {
        method: 'POST',
        body: {
          quantity_received: parseFloat(form.quantity_received),
          location_id: form.location_id || undefined,
          lot_number: form.lot_number || undefined,
          intake_type: form.intake_type,
        },
      });
      const batch = res?.movement?.batch;
      if (batch?.id) {
        setReceived([{ id: batch.id, ref: batch.batch_ref, item: line.item_name }]);
      } else {
        onReceived();
      }
    } catch (err) { setError(err.message); setSaving(false); }
  }

  if (received) return <ReceivedLabelsModal batches={received} onClose={onReceived} />;

  return (
    <Modal title={`Receive — ${line.item_name}`} onClose={onClose} width={480}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
          Ordered: <strong style={{ color: 'var(--text)' }}>{fmt(line.quantity_ordered)} {line.unit}</strong>
          {parseFloat(line.quantity_received ?? 0) > 0 && <> · Received so far: <strong style={{ color: 'var(--text)' }}>{fmt(line.quantity_received)} {line.unit}</strong></>}
          · Remaining: <strong style={{ color: 'var(--pld-green)' }}>{fmt(remaining)} {line.unit}</strong>
        </div>
        <Field label={`Quantity to receive (${line.unit}) *`}>
          <Input type="number" min="0.001" step="0.001" required value={form.quantity_received}
            onChange={e => set('quantity_received', e.target.value)} autoFocus />
        </Field>
        <Field label="Location *" hint={locations.length === 0 ? 'No locations set up yet — set up your warehouse first.' : ''}>
          {locations.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>Warehouse locations required before receiving stock.</p>
          ) : (
            <LocationSelect locations={locations} value={form.location_id} onChange={e => set('location_id', e.target.value)} required placeholder="— select location —" />
          )}
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Lot / Batch Number"><Input value={form.lot_number} onChange={e => set('lot_number', e.target.value)} placeholder="Optional" /></Field>
          <Field label="Intake Type">
            <SelectEl value={form.intake_type} onChange={e => set('intake_type', e.target.value)}>
              <option value="INTAKE_UK">UK Intake (bonded)</option>
              <option value="INITIAL_INTAKE">Duty paid / not bonded (not on the W1)</option>
              <option value="INTAKE_IMPORT">Import</option>
              <option value="INTAKE_EU">EU Import</option>
              <option value="INTAKE_OTHER">Other</option>
            </SelectEl>
          </Field>
        </div>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !form.quantity_received || (locations.length > 0 && !form.location_id)}>
            {saving ? 'Receiving…' : 'Confirm Receipt'}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Receive All Modal ────────────────────────────────────────────────────────
// "One table, receive all outstanding lines at once" — every line still
// owed on this PO in an editable table, one submit posts every row's stock
// movement + batch in a single transaction (POST /receive-lines). Rows
// default to their full remaining quantity and the line's own receiving
// location, and can be unchecked for anything that genuinely hasn't
// turned up yet in a partial delivery.
function ReceiveAllModal({ poId, lines, onClose, onReceived }) {
  const { data: locData } = useApi('/warehouse/locations');
  const locations = locData?.locations ?? [];

  const [rows, setRows] = useState(() => lines.map(l => {
    const remaining = parseFloat(l.quantity_ordered) - parseFloat(l.quantity_received ?? 0);
    return {
      line_id: l.id,
      item_name: l.item_name,
      unit: l.unit,
      remaining,
      checked: true,
      quantity_received: String(remaining),
      location_id: l.receive_location_id ?? '',
      lot_number: l.lot_number ?? '',
      intake_type: l.is_bonded ? 'INTAKE_UK' : 'INITIAL_INTAKE',
    };
  }));
  const [received, setReceived] = useState(null); // batches to offer labels for
  const [applyLocation, setApplyLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function updateRow(lineId, patch) {
    setRows(rs => rs.map(r => r.line_id === lineId ? { ...r, ...patch } : r));
  }

  function applyLocationToAll() {
    if (!applyLocation) return;
    setRows(rs => rs.map(r => ({ ...r, location_id: applyLocation })));
  }

  const checkedRows = rows.filter(r => r.checked);
  const canSubmit = checkedRows.length > 0 && checkedRows.every(r =>
    parseFloat(r.quantity_received) > 0 && (locations.length === 0 || r.location_id)
  );

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const res = await apiFetch(`/purchase-orders/${poId}/receive-lines`, {
        method: 'POST',
        body: {
          lines: checkedRows.map(r => ({
            line_id: r.line_id,
            quantity_received: parseFloat(r.quantity_received),
            location_id: r.location_id || undefined,
            lot_number: r.lot_number || undefined,
            intake_type: r.intake_type,
          })),
        },
      });
      // Results come back in the same order as the lines sent.
      const batches = (res?.results ?? []).map((r, idx) => ({
        id: r?.movement?.batch?.id,
        ref: r?.movement?.batch?.batch_ref,
        item: checkedRows[idx]?.item_name,
      })).filter(b => b.id);
      if (batches.length) setReceived(batches); else onReceived();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  if (received) return <ReceivedLabelsModal batches={received} onClose={onReceived} />;

  return (
    <Modal title={`Receive All — ${lines.length} outstanding line${lines.length === 1 ? '' : 's'}`} onClose={onClose} width={760}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {locations.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
            <div style={{ flex: 1 }}>
              <Field label="Set every row's location to">
                <LocationSelect locations={locations} value={applyLocation} onChange={e => setApplyLocation(e.target.value)} placeholder="— select location —" />
              </Field>
            </div>
            <Btn type="button" size="sm" variant="ghost" onClick={applyLocationToAll} disabled={!applyLocation}>Apply to all</Btn>
          </div>
        )}

        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                {['', 'Item', 'Qty received', 'Location', 'Lot / Batch #'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.line_id} style={{ borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none', opacity: r.checked ? 1 : 0.5 }}>
                  <td style={{ padding: '8px 10px' }}>
                    <input type="checkbox" checked={r.checked} onChange={e => updateRow(r.line_id, { checked: e.target.checked })} />
                  </td>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>
                    {r.item_name}
                    <div style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>Remaining: {fmt(r.remaining)} {r.unit}</div>
                  </td>
                  <td style={{ padding: '8px 10px', width: 110 }}>
                    <Input type="number" min="0.001" step="0.001" disabled={!r.checked}
                      value={r.quantity_received} onChange={e => updateRow(r.line_id, { quantity_received: e.target.value })} />
                  </td>
                  <td style={{ padding: '8px 10px', width: 180 }}>
                    {locations.length === 0 ? (
                      <span style={{ color: '#dc2626', fontSize: 12 }}>No locations set up</span>
                    ) : (
                      <LocationSelect locations={locations} value={r.location_id} disabled={!r.checked} onChange={e => updateRow(r.line_id, { location_id: e.target.value })} placeholder="— select —" />
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', width: 150 }}>
                    <Input value={r.lot_number} disabled={!r.checked} placeholder="Optional"
                      onChange={e => updateRow(r.line_id, { lot_number: e.target.value })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !canSubmit}>
            {saving ? 'Receiving…' : `Confirm Receipt (${checkedRows.length})`}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Add PO Line Modal ─────────────────────────────────────────────────────────
function AddPOLineModal({ poId, supplierId, onClose, onAdded }) {
  const { data: stockData } = useApi('/stock');
  const allStock = stockData?.stock_items ?? [];
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ quantity_ordered: '', unit_price: '', abv: '', supplier_reference: '', vat_rate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [priceHint, setPriceHint] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const filtered = allStock.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.sku.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 10);

  // Prefill the price as soon as an item is picked (and re-check whenever
  // the quantity changes) — same priority recipe costing already uses: a
  // quantity-break price for this PO's supplier at this quantity, else the
  // most recent PO price for this item, falling back to its standard cost.
  // So this doesn't sit blank, or stuck at a price that stopped applying
  // once you typed in a big enough quantity to clear a break.
  useEffect(() => {
    if (!selectedItem) { setPriceHint(null); return; }
    let cancelled = false;
    const params = new URLSearchParams();
    if (supplierId) params.set('supplier_id', supplierId);
    if (form.quantity_ordered) params.set('quantity', form.quantity_ordered);
    apiFetch(`/stock/${selectedItem.id}/suggested-price?${params.toString()}`).then(res => {
      if (cancelled) return;
      setPriceHint(res);
      if (res.unit_price != null) set('unit_price', String(res.unit_price));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedItem, supplierId, form.quantity_ordered]);

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch(`/purchase-orders/${poId}/lines`, {
        method: 'POST',
        body: {
          stock_item_id: selectedItem.id,
          quantity_ordered: parseFloat(form.quantity_ordered),
          unit_price: form.unit_price ? parseFloat(form.unit_price) : undefined,
          abv: form.abv ? parseFloat(form.abv) : undefined,
          supplier_reference: form.supplier_reference || undefined,
          vat_rate: form.vat_rate !== '' ? parseFloat(form.vat_rate) : null,
        },
      });
      onAdded();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title="Add Line Item" onClose={onClose} width={520}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Stock Item *">
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <Input value={search} onChange={e => { setSearch(e.target.value); setSelectedItem(null); setOpen(true); }}
                onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
                placeholder="Search stock items…" autoFocus />
              {selectedItem && <button type="button" onClick={() => { setSelectedItem(null); setSearch(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, padding: '0 6px' }}>×</button>}
            </div>
            {open && filtered.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 400, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)', maxHeight: 180, overflowY: 'auto', marginTop: 2 }}>
                {filtered.map(s => (
                  <div key={s.id} onMouseDown={() => { setSelectedItem(s); setSearch(s.name); setOpen(false); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <span style={{ fontWeight: 600 }}>{s.name}</span>
                    <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 12 }}>{s.sku} · {s.unit}</span>
                  </div>
                ))}
              </div>
            )}
            {selectedItem && <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pld-green)', fontWeight: 600 }}>✓ {selectedItem.sku} · {selectedItem.unit}</p>}
          </div>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label={`Qty ordered${selectedItem ? ` (${selectedItem.unit})` : ''} *`}>
            <Input type="number" min="0.001" step="0.001" required value={form.quantity_ordered} onChange={e => set('quantity_ordered', e.target.value)} />
          </Field>
          <Field label="Unit Price (£)"
            hint={priceHint?.unit_price != null
              ? (priceHint.source === 'price_break'
                  ? `Price break (${priceHint.price_break?.min_quantity}+)`
                  : priceHint.source === 'approved_supplier_price'
                    ? 'Approved supplier price'
                    : priceHint.source === 'purchase_order' ? `Suggested from ${priceHint.source_po_ref}` : 'Suggested from standard cost')
              : (selectedItem && priceHint ? 'No cost on file for this item yet' : undefined)}>
            <Input type="number" min="0" step="0.0001" value={form.unit_price} onChange={e => set('unit_price', e.target.value)} placeholder="0.00" />
          </Field>
          <Field label="ABV %">
            <Input type="number" min="0" max="100" step="0.1" value={form.abv} onChange={e => set('abv', e.target.value)} placeholder="e.g. 63.5" />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <Field label="Supplier Product Reference" hint="Their own code for this item — printed on the PO so they can match it up">
            <Input value={form.supplier_reference} onChange={e => set('supplier_reference', e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="VAT">
            <VatSelect value={form.vat_rate} onChange={v => set('vat_rate', v)} />
          </Field>
        </div>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !selectedItem || !form.quantity_ordered}>{saving ? 'Adding…' : 'Add Line'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Edit PO Line Modal ────────────────────────────────────────────────────────
// The simple path for "supplier came back with a revised price" — the PO
// keeps its same ref, the line is updated in place, and a reason can be
// recorded. What actually changed is kept as a change log (see the "History"
// link on the line) rather than needing a whole new PO to be raised.
function EditPOLineModal({ poId, line, onClose, onSaved }) {
  const [form, setForm] = useState({
    quantity_ordered: line.quantity_ordered ?? '',
    unit_price: line.unit_price ?? '',
    abv: line.abv ?? '',
    supplier_reference: line.supplier_reference ?? '',
    vat_rate: line.vat_rate ?? '',
    reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const priceChanged = form.unit_price !== '' && String(line.unit_price ?? '') !== String(form.unit_price);
  const qtyChanged = form.quantity_ordered !== '' && String(line.quantity_ordered ?? '') !== String(form.quantity_ordered);

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch(`/purchase-orders/${poId}/lines/${line.id}`, {
        method: 'PUT',
        body: {
          quantity_ordered: parseFloat(form.quantity_ordered),
          unit_price: form.unit_price !== '' ? parseFloat(form.unit_price) : null,
          abv: form.abv !== '' ? parseFloat(form.abv) : null,
          supplier_reference: form.supplier_reference || null,
          vat_rate: form.vat_rate !== '' ? parseFloat(form.vat_rate) : null,
          reason: (priceChanged || qtyChanged) ? (form.reason || null) : undefined,
        },
      });
      onSaved();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title={`Edit Line — ${line.item_name}`} onClose={onClose} width={480}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label={`Qty ordered (${line.unit}) *`}>
            <Input type="number" min="0.001" step="0.001" required value={form.quantity_ordered} onChange={e => set('quantity_ordered', e.target.value)} />
          </Field>
          <Field label="Unit Price (£)">
            <Input type="number" min="0" step="0.0001" value={form.unit_price} onChange={e => set('unit_price', e.target.value)} placeholder="0.00" />
          </Field>
          <Field label="ABV %">
            <Input type="number" min="0" max="100" step="0.1" value={form.abv} onChange={e => set('abv', e.target.value)} placeholder="e.g. 63.5" />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <Field label="Supplier Product Reference">
            <Input value={form.supplier_reference} onChange={e => set('supplier_reference', e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="VAT">
            <VatSelect value={form.vat_rate} onChange={v => set('vat_rate', v)} />
          </Field>
        </div>
        {(priceChanged || qtyChanged) && (
          <Field label="Reason for change" hint="Recorded in this line's history — e.g. 'supplier revised price'">
            <Input value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="Optional but useful" />
          </Field>
        )}
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !form.quantity_ordered}>{saving ? 'Saving…' : 'Save Changes'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Line History Modal ────────────────────────────────────────────────────────
function LineHistoryModal({ line, onClose }) {
  const revisions = line.revisions ?? [];
  return (
    <Modal title={`History — ${line.item_name}`} onClose={onClose} width={480}>
      {revisions.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 14 }}>No changes recorded for this line.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {revisions.map((r, i) => (
            <div key={i} style={{ borderLeft: '2px solid var(--border)', paddingLeft: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {fmtDate(r.changed_at)} {r.changed_at ? new Date(r.changed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''} · {r.changed_by_name || 'Unknown'}
              </div>
              <div style={{ fontSize: 13, marginTop: 2 }}>
                {String(r.previous?.unit_price ?? '') !== String(r.new?.unit_price ?? '') && (
                  <div>Unit price: <strike style={{ color: 'var(--text-muted)' }}>£{fmt(r.previous?.unit_price ?? 0)}</strike> → <strong>£{fmt(r.new?.unit_price ?? 0)}</strong></div>
                )}
                {String(r.previous?.quantity_ordered ?? '') !== String(r.new?.quantity_ordered ?? '') && (
                  <div>Quantity: <strike style={{ color: 'var(--text-muted)' }}>{fmt(r.previous?.quantity_ordered ?? 0)}</strike> → <strong>{fmt(r.new?.quantity_ordered ?? 0)}</strong></div>
                )}
                {r.new?.reason && <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>"{r.new.reason}"</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ── PO Detail ─────────────────────────────────────────────────────────────────
function PODetail({ poId, onBack }) {
  const { data, loading, error, refetch } = useApi(`/purchase-orders/${poId}`, [poId]);
  const po = data?.purchase_order;
  const [addingLine, setAddingLine] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [historyLine, setHistoryLine] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const [receivingAll, setReceivingAll] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const { data: invoiceData, refetch: refetchInvoices } = useApi(`/purchase-orders/${poId}/invoices`, [poId]);
  const [uploadingConfirmation, setUploadingConfirmation] = useState(false);
  const [dismissedCharges, setDismissedCharges] = useState(new Set());
  const [removingLineId, setRemovingLineId] = useState(null);
  const [addingChargeKey, setAddingChargeKey] = useState(null);
  const { data: confirmationData, refetch: refetchConfirmations } = useApi(`/purchase-orders/${poId}/confirmations`, [poId]);
  const [editingVat, setEditingVat] = useState(false);
  const [vatInput, setVatInput] = useState('');
  const [savingVat, setSavingVat] = useState(false);

  async function updateStatus(status) {
    try {
      await apiFetch(`/purchase-orders/${poId}/status`, { method: 'PATCH', body: { status } });
      refetch();
    } catch (err) { alert(err.message); }
  }

  async function cancelPO() {
    if (!(await uiConfirm(`Cancel ${po.po_ref}? This can't be undone.`))) return;
    await updateStatus('cancelled');
  }

  function openVatEditor() {
    setVatInput(po.vat_rate != null ? String(po.vat_rate) : '');
    setEditingVat(true);
  }

  async function saveVatRate() {
    setSavingVat(true);
    try {
      await apiFetch(`/purchase-orders/${poId}`, {
        method: 'PUT',
        body: { vat_rate: vatInput === '' ? '' : parseFloat(vatInput) },
      });
      setEditingVat(false);
      refetch();
    } catch (err) { alert(err.message); }
    finally { setSavingVat(false); }
  }

  async function sendToSupplier() {
    const isResend = po.status !== 'draft';
    if (isResend && !(await uiConfirm(`Resend ${po.po_ref} to ${po.supplier_name}${po.supplier_email ? ` (${po.supplier_email})` : ''}?`))) return;
    setSending(true);
    try {
      const res = await apiFetch(`/purchase-orders/${poId}/send`, { method: 'POST' });
      if (res.emailed) {
        alert(`PO ${isResend ? 're-sent' : 'sent'} to ${po.supplier_name} by email.`);
      } else if (res.emailError) {
        alert(isResend
          ? `Resend failed: ${res.emailError}`
          : `PO marked as sent, but email failed: ${res.emailError}\n\nUse Preview to print and send it manually.`);
      } else {
        alert(isResend
          ? `Couldn't resend — no supplier email on file.`
          : `PO marked as sent. No supplier email on file — use Preview to print and send it manually.`);
      }
      refetch();
    } catch (err) { alert(err.message); }
    finally { setSending(false); }
  }

  async function openPrintPreview() {
    // This is an authenticated route (Bearer token, no cookie session), so a
    // plain window.open() straight to the API URL sends no Authorization
    // header and just 401s. Fetch it ourselves with the token, then hand
    // the browser a blob: URL to open instead.
    try {
      const res = await fetch(`/api/purchase-orders/${poId}/html`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed to load print view (HTTP ${res.status})`);
      const html = await res.text();
      const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      const win = window.open(blobUrl, '_blank');
      if (!win) throw new Error('Pop-up blocked — allow pop-ups for this site to use Preview.');
      // Release the blob once the new tab has had a chance to load it.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      alert(err.message);
    }
  }

  async function openGoodsReceiptPreview() {
    // Same authenticated-blob pattern as Print/Preview — a blank goods-in
    // checking sheet for this PO, with a line per item and space to write
    // the quantity received, batch/lot number, expiry and condition against
    // each one when the delivery actually arrives.
    try {
      const res = await fetch(`/api/purchase-orders/${poId}/goods-receipt-html`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed to load goods receipt (HTTP ${res.status})`);
      const html = await res.text();
      const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      const win = window.open(blobUrl, '_blank');
      if (!win) throw new Error('Pop-up blocked — allow pop-ups for this site to use Goods Receipt.');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      alert(err.message);
    }
  }

  // A confirmation said this line was never dispatched (out of stock, sold
  // elsewhere, whatever) — remove it from this PO rather than leaving it
  // sitting there priced and quantified as if it were still coming.
  async function removeNotDispatchedLine(line) {
    if (!(await uiConfirm(`Remove "${line.item_name}" from this PO? The supplier's confirmation said it wasn't dispatched.`))) return;
    setRemovingLineId(line.id);
    try {
      await apiFetch(`/purchase-orders/${poId}/lines/${line.id}`, {
        method: 'DELETE',
        body: { reason: 'Not dispatched per supplier confirmation' },
      });
      refetch();
    } catch (err) { alert(err.message); }
    finally { setRemovingLineId(null); }
  }

  // Service lines (e.g. the standard Delivery Charge auto-added for a
  // supplier) are just money, not physical stock — safe to let someone
  // take one off a PO directly, unlike a real goods line.
  // Any line with nothing received yet can come off the PO (the server
  // refuses once stock has been checked in against it).
  async function removeServiceLine(line) {
    if (!(await uiConfirm(`Remove "${line.item_name}" from this PO?`))) return;
    setRemovingLineId(line.id);
    try {
      await apiFetch(`/purchase-orders/${poId}/lines/${line.id}`, { method: 'DELETE' });
      refetch();
    } catch (err) { alert(err.message); }
    finally { setRemovingLineId(null); }
  }

  // A charge (delivery/carriage) showed up on the confirmation but was never
  // added as a line on the PO — add it now as a one-off line against the
  // shared "Delivery Charge" service item, same one used for standard
  // per-supplier delivery charges.
  async function addDeliveryCharge(candidate) {
    setAddingChargeKey(candidate.key);
    try {
      const found = await apiFetch(`/stock?search=${encodeURIComponent('delivery charge')}&category=service`);
      const item = (found.stock_items || []).find(i => i.name.toLowerCase() === 'delivery charge') || (found.stock_items || [])[0];
      if (!item) throw new Error('No "Delivery Charge" stock item found — add one under Stock first.');
      await apiFetch(`/purchase-orders/${poId}/lines`, {
        method: 'POST',
        body: { stock_item_id: item.id, quantity_ordered: 1, unit_price: candidate.amount },
      });
      refetch();
    } catch (err) { alert(err.message); }
    finally { setAddingChargeKey(null); }
  }

  function dismissChargeCandidate(key) {
    setDismissedCharges(s => new Set([...s, key]));
  }

  if (loading) return <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>;
  if (error || !po) return <p style={{ color: '#dc2626', fontSize: 14 }}>Failed to load purchase order.</p>;

  const canEdit = ['draft', 'sent'].includes(po.status);
  // Unreceived lines can still come off once the supplier has confirmed or part-delivered.
  const canRemoveLines = ['draft', 'sent', 'confirmed', 'partial'].includes(po.status);
  const canReceive = ['confirmed', 'partial'].includes(po.status);
  const outstandingLines = (po.lines ?? []).filter((l) =>
    parseFloat(l.quantity_ordered) - parseFloat(l.quantity_received ?? 0) > 0
  );
  const totalValue = (po.lines ?? []).reduce((sum, l) => sum + (parseFloat(l.unit_price ?? 0) * parseFloat(l.quantity_ordered)), 0);
  // VAT per line, split by rate (worked out by the server: line rate, else the PO's, else the company default).
  const vatSummary = po.vat_summary || { byRate: [], gross: totalValue, base: null };
  const poOwnRate = po.vat_rate != null && po.vat_rate !== '' ? parseFloat(po.vat_rate) : null;
  const grandTotal = vatSummary.gross;

  // ── Confirmation follow-ups ───────────────────────────────────────────────
  // Things an applied pro-forma/invoice flagged that still need a human
  // decision: a line the supplier said wasn't dispatched (still sitting on
  // the PO as if it were), and any charge on the confirmation (e.g. a
  // delivery/carriage charge) that never made it onto the PO as a line.
  const appliedConfirmations = (confirmationData?.confirmations ?? []).filter(c => c.status === 'applied');
  const poLineIds = new Set((po.lines ?? []).map(l => l.id));
  const notDispatchedLines = [];
  const seenNotDispatched = new Set();
  const deliveryChargeCandidates = [];
  const seenCharges = new Set();
  for (const c of appliedConfirmations) {
    const parsed = c.parsed_json || {};
    for (const pl of parsed.lines || []) {
      if (pl.dispatched === false && pl.po_line_id && poLineIds.has(pl.po_line_id) && !seenNotDispatched.has(pl.po_line_id)) {
        seenNotDispatched.add(pl.po_line_id);
        const line = po.lines.find(l => l.id === pl.po_line_id);
        if (line) notDispatchedLines.push({ line, confirmation: c, description: pl.description_raw });
      }
    }
    for (const raw of parsed.unmatched_lines || []) {
      if (!/deliver|carriage|freight|courier|dpd|ups|fedex/i.test(raw)) continue;
      const match = raw.match(/([\d,]+\.\d{2})(?!.*[\d,]+\.\d{2})/); // last money-shaped number in the line
      if (!match) continue;
      const amount = parseFloat(match[1].replace(/,/g, ''));
      const key = `${c.id}:${raw}`;
      const alreadyOnPo = (po.lines ?? []).some(l => /delivery/i.test(l.item_name) && Math.abs(parseFloat(l.unit_price ?? 0) - amount) < 0.005);
      if (!alreadyOnPo && !seenCharges.has(key) && !dismissedCharges.has(key)) {
        seenCharges.add(key);
        deliveryChargeCandidates.push({ confirmation: c, raw, amount, key });
      }
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {addingLine && <AddPOLineModal poId={poId} supplierId={po.supplier_id} onClose={() => setAddingLine(false)} onAdded={() => { setAddingLine(false); refetch(); }} />}
      {editingLine && <EditPOLineModal poId={poId} line={editingLine} onClose={() => setEditingLine(null)} onSaved={() => { setEditingLine(null); refetch(); }} />}
      {historyLine && <LineHistoryModal line={historyLine} onClose={() => setHistoryLine(null)} />}
      {receiving && <ReceiveLineModal poId={poId} line={receiving} onClose={() => setReceiving(null)} onReceived={() => { setReceiving(null); refetch(); }} />}
      {receivingAll && <ReceiveAllModal poId={poId} lines={outstandingLines} onClose={() => setReceivingAll(false)} onReceived={() => { setReceivingAll(false); refetch(); }} />}
      {uploadingInvoice && <UploadInvoiceModal poId={poId} onClose={() => setUploadingInvoice(false)} onUploaded={() => { setUploadingInvoice(false); refetchInvoices(); }} />}
      {uploadingConfirmation && <UploadConfirmationModal poId={poId} onClose={() => setUploadingConfirmation(false)} onApplied={() => { setUploadingConfirmation(false); refetch(); refetchConfirmations(); }} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0 }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16 }}>{po.po_ref}</span>
            <POBadge status={po.status} />
          </div>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {po.supplier_name}
            {po.order_date ? ` · Ordered ${fmtDate(po.order_date)}` : ''}
            {po.expected_delivery_date ? ` · Expected ${fmtDate(po.expected_delivery_date)}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn size="sm" variant="ghost" onClick={openPrintPreview}>👁 Preview</Btn>
          <PrintButton docType="purchase_order" path={`/purchase-orders/${poId}/html`} title={`PO ${po.po_ref}`} />
          {['confirmed', 'partial', 'received'].includes(po.status) && (
            <>
              <Btn size="sm" variant="ghost" onClick={openGoodsReceiptPreview}>📋 Goods Receipt</Btn>
              <PrintButton docType="goods_receipt" path={`/purchase-orders/${poId}/goods-receipt-html`} title={`Goods receipt ${po.po_ref}`} label="Print GRN" />
            </>
          )}
          {po.status === 'draft' && <Btn size="sm" onClick={sendToSupplier} disabled={sending}>{sending ? 'Sending…' : '✉ Send to Supplier'}</Btn>}
          {po.status === 'draft' && (
            <Btn size="sm" variant="ghost" title="Ordered another way (phone, the supplier's website…) — move the PO on without emailing it"
              onClick={async () => { if ((await uiConfirm(`Mark ${po.po_ref} as sent without emailing ${po.supplier_name}?`))) updateStatus('sent'); }}>
              Mark as sent (no email)
            </Btn>
          )}
          {['sent', 'confirmed', 'partial'].includes(po.status) && <Btn size="sm" variant="ghost" onClick={sendToSupplier} disabled={sending}>{sending ? 'Sending…' : '↻ Resend to Supplier'}</Btn>}
          {['draft', 'sent'].includes(po.status) && (
            <Btn size="sm" variant="ghost" title="The supplier has confirmed the order — lets you receive against it"
              onClick={async () => { if (po.status === 'sent' || (await uiConfirm(`Mark ${po.po_ref} as confirmed without emailing ${po.supplier_name}?`))) updateStatus('confirmed'); }}>
              {po.status === 'draft' ? 'Mark as confirmed (no email)' : 'Confirm'}
            </Btn>
          )}
          {canEdit && <Btn size="sm" variant="ghost" onClick={() => setAddingLine(true)}>+ Add Line</Btn>}
          {['draft', 'sent', 'confirmed', 'partial'].includes(po.status) && (
            <Btn size="sm" variant="ghost" onClick={cancelPO} style={{ color: '#dc2626' }}>Cancel PO</Btn>
          )}
        </div>
      </div>

      {/* Notes */}
      {po.notes && (
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
          {po.notes}
        </div>
      )}

      {/* Confirmation follow-ups — things an applied pro-forma/invoice flagged
          that still need a human decision */}
      {(notDispatchedLines.length > 0 || deliveryChargeCandidates.length > 0) && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            From the pro-forma / invoice — needs your input
          </div>
          {notDispatchedLines.map(({ line, description }) => (
            <div key={line.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
              <span style={{ color: '#78716c' }}>
                <strong style={{ color: '#57534e' }}>{line.item_name}</strong> — supplier confirmation said this wasn't dispatched.
                {description && <span style={{ fontSize: 12 }}> ({description})</span>}
              </span>
              <Btn size="sm" variant="ghost" disabled={removingLineId === line.id} onClick={() => removeNotDispatchedLine(line)} style={{ flexShrink: 0 }}>
                {removingLineId === line.id ? 'Removing…' : 'Remove from PO'}
              </Btn>
            </div>
          ))}
          {deliveryChargeCandidates.map((candidate) => (
            <div key={candidate.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
              <span style={{ color: '#78716c' }}>
                Delivery charge on confirmation: <strong style={{ color: '#57534e' }}>£{candidate.amount.toFixed(2)}</strong> — not yet on this PO.
              </span>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <Btn size="sm" variant="ghost" onClick={() => dismissChargeCandidate(candidate.key)}>Dismiss</Btn>
                <Btn size="sm" disabled={addingChargeKey === candidate.key} onClick={() => addDeliveryCharge(candidate)}>
                  {addingChargeKey === candidate.key ? 'Adding…' : '+ Add to PO'}
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lines */}
      <div>
        <SectionHeader label={`Line Items${po.lines?.length ? ` (${po.lines.length})` : ''}`}
          action={(
            <div style={{ display: 'flex', gap: 8 }}>
              {canReceive && outstandingLines.length > 1 && (
                <Btn size="sm" onClick={() => setReceivingAll(true)}>Receive All ({outstandingLines.length})</Btn>
              )}
              {canEdit && <Btn size="sm" onClick={() => setAddingLine(true)}>+ Add Line</Btn>}
            </div>
          )} />
        {!po.lines?.length ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No lines yet — add items to this PO.</p>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                  {['Item', 'SKU', 'Supplier Ref', 'Ordered', 'Received', 'Outstanding', 'Unit Price', 'VAT', 'ABV', ''].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: ['Ordered','Received','Outstanding','Unit Price','VAT','ABV'].includes(h) ? 'right' : 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {po.lines.map((l, i) => {
                  const received = parseFloat(l.quantity_received ?? 0);
                  const ordered  = parseFloat(l.quantity_ordered);
                  const outstanding = ordered - received;
                  const done = outstanding <= 0;
                  return (
                    <tr key={l.id} style={{ borderBottom: i < po.lines.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{l.item_name}</td>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{l.sku}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{l.supplier_reference || '—'}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>{fmt(ordered)} {l.unit}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: received > 0 ? '#166534' : 'var(--text-muted)' }}>{fmt(received)} {l.unit}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: done ? 'var(--text-muted)' : '#dc2626', fontWeight: done ? 400 : 600 }}>
                        {done ? '✓ Done' : `${fmt(outstanding)} ${l.unit}`}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-muted)' }}>
                        {l.unit_price ? `£${fmt(l.unit_price, 4)}` : '—'}
                        {l.revisions?.length > 0 && (
                          <button onClick={() => setHistoryLine(l)} title="Price/quantity change history" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pld-green)', fontSize: 11, marginLeft: 6, padding: 0, textDecoration: 'underline' }}>
                            history ({l.revisions.length})
                          </button>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: l.vat_rate != null ? 'var(--text)' : 'var(--text-muted)' }} title={l.vat_rate != null ? 'Set on this line' : "The PO's rate"}>
                        {l.effective_vat_rate != null ? `${Number(l.effective_vat_rate)}%` : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-muted)' }}>{l.abv ? `${fmtAbv(l.abv)}%` : '—'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {canReceive && !done && (
                            <Btn size="sm" onClick={() => setReceiving(l)}>Receive</Btn>
                          )}
                          {canEdit && (
                            <Btn size="sm" variant="ghost" onClick={() => setEditingLine(l)}>Edit</Btn>
                          )}
                          {canRemoveLines && (l.category === 'service' || !(parseFloat(l.quantity_received) > 0)) && (
                            <Btn size="sm" variant="ghost" disabled={removingLineId === l.id} onClick={() => removeServiceLine(l)} style={{ color: '#dc2626' }}>
                              {removingLineId === l.id ? '…' : 'Remove'}
                            </Btn>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {totalValue > 0 && (
                <tfoot>
                  <tr style={{ background: 'var(--surface-2)', borderTop: '2px solid var(--border)' }}>
                    <td colSpan={6} style={{ padding: '9px 14px', fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>Subtotal (ex VAT)</td>
                    <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 700, fontSize: 14 }}>£{fmt(totalValue)}</td>
                    <td colSpan={3} />
                  </tr>
                  {vatSummary.byRate.map(g => (
                    <tr key={g.rate} style={{ background: 'var(--surface-2)' }}>
                      <td colSpan={6} style={{ padding: '9px 14px', fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>
                        VAT {g.rate}%{vatSummary.byRate.length > 1 ? ` on £${fmt(g.net, 2)}` : ''}
                      </td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 700, fontSize: 14 }}>£{fmt(g.vat, 2)}</td>
                      <td colSpan={3} />
                    </tr>
                  ))}
                  <tr style={{ background: 'var(--surface-2)', borderTop: '2px solid var(--border)' }}>
                    <td colSpan={6} style={{ padding: '9px 14px', fontSize: 13, color: 'var(--text)', fontWeight: 700 }}>Total (inc VAT)</td>
                    <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 700, fontSize: 15 }}>£{fmt(grandTotal, 2)}</td>
                    <td colSpan={3} />
                  </tr>
                  <tr>
                    <td colSpan={10} style={{ padding: '7px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
                      {editingVat ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          VAT for lines without their own rate
                          <VatSelect value={vatInput} onChange={setVatInput} blankLabel={`Company default${po.company_default_vat_rate != null ? ` (${Number(po.company_default_vat_rate)}%)` : ''}`} style={{ width: 190, padding: '3px 6px', fontSize: 13 }} />
                          <Btn size="sm" disabled={savingVat} onClick={saveVatRate}>{savingVat ? '…' : 'Save'}</Btn>
                          <Btn size="sm" variant="ghost" disabled={savingVat} onClick={() => setEditingVat(false)}>Cancel</Btn>
                        </span>
                      ) : (
                        <span>
                          Lines without their own rate: {vatSummary.base != null ? `${Number(vatSummary.base)}%` : '—'}{poOwnRate == null ? ' (company default)' : ''}. Set a line's rate with Edit.
                          {canEdit && (
                            <button onClick={openVatEditor} title="Change the PO's VAT rate" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pld-green)', fontSize: 11, marginLeft: 6, padding: 0, textDecoration: 'underline' }}>
                              change
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* Pro-forma / Invoices (AI-read) */}
      <div>
        <SectionHeader label={`Pro-forma / Invoices${confirmationData?.confirmations?.length ? ` (${confirmationData.confirmations.length})` : ''}`}
          action={<Btn size="sm" onClick={() => setUploadingConfirmation(true)}>Upload Pro-forma / Invoice</Btn>} />
        {!confirmationData?.confirmations?.length ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Nothing uploaded yet — upload the supplier's pro-forma invoice, invoice, or order acknowledgement and Blake will read it and propose price/quantity updates against this PO.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {confirmationData.confirmations.map(c => (
              <ConfirmationRow key={c.id} poId={poId} confirmation={c} onChanged={() => { refetchConfirmations(); refetch(); }} />
            ))}
          </div>
        )}
      </div>

      {/* Documents (plain attachment — delivery notes, certs, anything else) */}
      <div>
        <SectionHeader label={`Documents${invoiceData?.invoices?.length ? ` (${invoiceData.invoices.length})` : ''}`}
          action={<Btn size="sm" onClick={() => setUploadingInvoice(true)}>+ Documents</Btn>} />
        {!invoiceData?.invoices?.length ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No documents attached yet — drop delivery notes, certificates, or anything else worth keeping with this PO.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {invoiceData.invoices.map(inv => (
              <InvoiceRow key={inv.id} poId={poId} invoice={inv} onDeleted={refetchInvoices} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Invoice Row ───────────────────────────────────────────────────────────────
function InvoiceRow({ poId, invoice, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  async function viewFile() {
    try {
      const res = await fetch(`/api/purchase-orders/${poId}/invoices/${invoice.id}/file`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed to load invoice file (HTTP ${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, '_blank');
      if (!win) throw new Error('Pop-up blocked — allow pop-ups for this site to view invoice files.');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) { alert(err.message); }
  }

  async function handleDelete() {
    if (!(await uiConfirm(`Remove invoice ${invoice.invoice_number || invoice.file_name}?`))) return;
    setDeleting(true);
    try {
      await apiFetch(`/purchase-orders/${poId}/invoices/${invoice.id}`, { method: 'DELETE' });
      onDeleted();
    } catch (err) { alert(err.message); setDeleting(false); }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', background: 'var(--surface)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{invoice.invoice_number || invoice.file_name}</span>
          {invoice.invoice_date && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(invoice.invoice_date)}</span>}
          {invoice.invoice_total != null && <span style={{ fontSize: 13 }}>£{fmt(invoice.invoice_total)}</span>}
          {invoice.matches_po_total === false && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 99, padding: '2px 8px' }}>
              ⚠ doesn't match PO total
            </span>
          )}
          {invoice.matches_po_total === true && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#166534', background: '#dcfce7', borderRadius: 99, padding: '2px 8px' }}>
              ✓ matches PO total
            </span>
          )}
        </div>
        {invoice.notes && <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>{invoice.notes}</p>}
        <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>Uploaded by {invoice.uploaded_by_name || 'Unknown'} · {fmtDate(invoice.uploaded_at)}</p>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <Btn size="sm" variant="ghost" onClick={viewFile}>View</Btn>
        <Btn size="sm" variant="danger" onClick={handleDelete} disabled={deleting}>{deleting ? '…' : 'Remove'}</Btn>
      </div>
    </div>
  );
}

// ── Upload Invoice Modal ──────────────────────────────────────────────────────
function UploadInvoiceModal({ poId, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ invoice_number: '', invoice_date: '', invoice_total: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file) { setError('Choose a file to upload.'); return; }
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (form.invoice_number) fd.append('invoice_number', form.invoice_number);
      if (form.invoice_date) fd.append('invoice_date', form.invoice_date);
      if (form.invoice_total) fd.append('invoice_total', form.invoice_total);
      if (form.notes) fd.append('notes', form.notes);

      const res = await fetch(`/api/purchase-orders/${poId}/invoices`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Upload failed (HTTP ${res.status})`);
      }
      onUploaded();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title="Add Document" onClose={onClose} width={480}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="File (PDF or image) *">
          <input type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] ?? null)} required />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Reference / Invoice Number">
            <Input value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="Date">
            <Input type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} />
          </Field>
        </div>
        <Field label="Total (£)" hint="If this is an invoice, checked against the PO's own total — flagged if it doesn't match">
          <Input type="number" min="0" step="0.01" value={form.invoice_total} onChange={e => set('invoice_total', e.target.value)} placeholder="0.00" />
        </Field>
        <Field label="Notes">
          <Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional" />
        </Field>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !file}>{saving ? 'Uploading…' : 'Add Document'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Confirmation Row ─────────────────────────────────────────────────────────
function ConfirmationRow({ poId, confirmation, onChanged }) {
  async function viewFile() {
    try {
      const res = await fetch(`/api/purchase-orders/${poId}/confirmations/${confirmation.id}/file`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed to load file (HTTP ${res.status})`);
      const blob = await res.blob();
      const win = window.open(URL.createObjectURL(blob), '_blank');
      if (!win) throw new Error('Pop-up blocked — allow pop-ups for this site to view uploaded files.');
    } catch (err) { alert(err.message); }
  }

  async function dismiss() {
    if (!(await uiConfirm('Dismiss this document without applying it?'))) return;
    try {
      await apiFetch(`/purchase-orders/${poId}/confirmations/${confirmation.id}/dismiss`, { method: 'POST' });
      onChanged();
    } catch (err) { alert(err.message); }
  }

  const statusColor = { pending_review: '#b45309', applied: '#166534', dismissed: 'var(--text-muted)' }[confirmation.status] || 'var(--text-muted)';
  const statusLabel = { pending_review: 'Needs review', applied: 'Applied', dismissed: 'Dismissed' }[confirmation.status] || confirmation.status;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{confirmation.supplier_order_number || confirmation.file_name}</span>
          {confirmation.confirmation_date && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>{fmtDate(confirmation.confirmation_date)}</span>}
          <span style={{ fontSize: 11, fontWeight: 700, color: statusColor, marginLeft: 8, textTransform: 'uppercase', letterSpacing: '.03em' }}>{statusLabel}</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Btn size="sm" variant="ghost" onClick={viewFile}>View File</Btn>
          {confirmation.status === 'pending_review' && (
            <Btn size="sm" variant="ghost" onClick={dismiss} style={{ color: '#dc2626' }}>Dismiss</Btn>
          )}
        </div>
      </div>
      <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>Uploaded by {confirmation.uploaded_by_name || 'Unknown'} · {fmtDate(confirmation.uploaded_at)}</p>
    </div>
  );
}

// ── Upload Pro-forma / Invoice Modal ────────────────────────────────────────
// Two steps in one modal: upload the document (AI parses it against the
// PO's own lines, and checks the supplier's printed details against what's
// on file), then review the proposed diff and choose what to apply.
// Nothing changes on the PO — or the supplier record — until reviewed.
function UploadConfirmationModal({ poId, onClose, onApplied }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { confirmation, po_lines }
  const [selections, setSelections] = useState({}); // po_line_id -> { apply_quantity, apply_price, update_standard_cost }
  const [applying, setApplying] = useState(false);
  const [detailFlags, setDetailFlags] = useState([]); // supplier detail drift, resolved as they're actioned

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) { setError('Choose a file to upload.'); return; }
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/purchase-orders/${poId}/confirmations`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Upload failed (HTTP ${res.status})`);

      const parsed = typeof body.confirmation.parsed_json === 'string' ? JSON.parse(body.confirmation.parsed_json) : body.confirmation.parsed_json;
      const initial = {};
      (parsed.lines || []).forEach(l => {
        if (l.po_line_id && l.dispatched !== false) {
          initial[l.po_line_id] = { apply_quantity: l.confirmed_quantity != null, apply_price: l.confirmed_unit_price != null, update_standard_cost: false };
        }
      });
      setSelections(initial);
      setDetailFlags(body.detail_flags || []);
      setResult({ confirmation: { ...body.confirmation, parsed_json: parsed }, po_lines: body.po_lines });
    } catch (err) { setError(err.message); }
    finally { setUploading(false); }
  }

  async function resolveDetailFlag(flag, action) {
    try {
      await apiFetch(`/suppliers/${flag.supplier_id}/detail-flags/${flag.id}/${action}`, { method: 'POST' });
      setDetailFlags(fs => fs.filter(f => f.id !== flag.id));
    } catch (err) { alert(err.message); }
  }

  function toggle(lineId, key) {
    setSelections(s => ({ ...s, [lineId]: { ...s[lineId], [key]: !s[lineId]?.[key] } }));
  }

  async function handleApply(markConfirmed) {
    setApplying(true); setError('');
    try {
      const lines = Object.entries(selections)
        .filter(([, sel]) => sel.apply_quantity || sel.apply_price)
        .map(([po_line_id, sel]) => ({ po_line_id, ...sel }));
      await apiFetch(`/purchase-orders/${poId}/confirmations/${result.confirmation.id}/apply`, {
        method: 'POST',
        body: { lines, mark_confirmed: markConfirmed },
      });
      onApplied();
    } catch (err) { setError(err.message); setApplying(false); }
  }

  if (!result) {
    return (
      <Modal title="Upload Pro-forma / Invoice" onClose={onClose} width={480}>
        <form onSubmit={handleUpload} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
            Upload the supplier's pro-forma invoice, invoice, or order acknowledgement (PDF or image). Blake will read it and match it against this PO's lines — you'll review the changes before anything is applied.
          </p>
          <Field label="File (PDF or image) *">
            <input type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] ?? null)} required />
          </Field>
          {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" disabled={uploading || !file}>{uploading ? 'Reading document…' : 'Read Document'}</Btn>
          </div>
        </form>
      </Modal>
    );
  }

  const parsed = result.confirmation.parsed_json;
  const linesById = Object.fromEntries(result.po_lines.map(l => [l.id, l]));
  const matched = (parsed.lines || []).filter(l => l.po_line_id && linesById[l.po_line_id]);

  return (
    <Modal title="Review Pro-forma / Invoice" onClose={onClose} width={640}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {(parsed.supplier_order_number || parsed.confirmation_date) && (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
            {parsed.supplier_order_number && <>Supplier reference: <strong>{parsed.supplier_order_number}</strong>{' · '}</>}
            {parsed.confirmation_date && <>Dated {fmtDate(parsed.confirmation_date)}</>}
          </p>
        )}
        {parsed.flags?.length > 0 && (
          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 'var(--radius)', padding: '8px 12px', fontSize: 12.5 }}>
            {parsed.flags.map((f, i) => <div key={i}>⚠ {f}</div>)}
          </div>
        )}

        {detailFlags.length > 0 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 6 }}>Supplier details differ from what's on file</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {detailFlags.map(flag => (
                <div key={flag.id} style={{ border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 'var(--radius)', padding: '8px 12px', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div>
                    <strong style={{ textTransform: 'capitalize' }}>{flag.field_name.replace(/_/g, ' ')}</strong>:{' '}
                    <span style={{ color: 'var(--text-muted)' }}>{flag.current_value || '(blank)'}</span>{' → '}
                    <strong>{flag.observed_value}</strong>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Btn size="sm" onClick={() => resolveDetailFlag(flag, 'apply')}>Update supplier record</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => resolveDetailFlag(flag, 'dismiss')}>Ignore</Btn>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {matched.map(l => {
            const line = linesById[l.po_line_id];
            const sel = selections[l.po_line_id] || {};
            const qtyChanged = l.confirmed_quantity != null && String(l.confirmed_quantity) !== String(line.quantity_ordered);
            const priceChanged = l.confirmed_unit_price != null && String(l.confirmed_unit_price) !== String(line.unit_price);

            if (l.dispatched === false) {
              return (
                <div key={l.po_line_id} style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{line.item_name}</div>
                  <div style={{ fontSize: 12.5, color: '#991b1b', marginTop: 2 }}>Not dispatched{l.note ? ` — ${l.note}` : ''}. Will be flagged on the PO as backordered; price/quantity left unchanged.</div>
                </div>
              );
            }

            return (
              <div key={l.po_line_id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{line.item_name}</div>
                {l.conversion_note && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Supplier's units: {l.conversion_note}</div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                  {l.confirmed_quantity != null && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!sel.apply_quantity} onChange={() => toggle(l.po_line_id, 'apply_quantity')} />
                      Quantity: {qtyChanged ? <><strike style={{ color: 'var(--text-muted)' }}>{fmt(line.quantity_ordered)}</strike> → <strong>{fmt(l.confirmed_quantity)}</strong></> : <>{fmt(l.confirmed_quantity)} (unchanged)</>} {line.unit}
                    </label>
                  )}
                  {l.confirmed_unit_price != null && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!sel.apply_price} onChange={() => toggle(l.po_line_id, 'apply_price')} />
                      Unit price: {priceChanged ? <><strike style={{ color: 'var(--text-muted)' }}>£{fmt(line.unit_price ?? 0, 4)}</strike> → <strong>£{fmt(l.confirmed_unit_price, 4)}</strong></> : <>£{fmt(l.confirmed_unit_price, 4)} (unchanged)</>}{line.unit ? ` per ${line.unit}` : ''}
                    </label>
                  )}
                  {sel.apply_price && priceChanged && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-muted)', marginLeft: 24, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!sel.update_standard_cost} onChange={() => toggle(l.po_line_id, 'update_standard_cost')} />
                      Also update this item's Standard Cost to £{fmt(l.confirmed_unit_price, 4)}
                    </label>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {parsed.unmatched_lines?.length > 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            <strong>Not matched to any PO line:</strong> {parsed.unmatched_lines.join(', ')}
          </div>
        )}

        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="button" disabled={applying} onClick={() => handleApply(true)}>{applying ? 'Applying…' : 'Apply Selected'}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── PO List ───────────────────────────────────────────────────────────────────
function POList({ onSelectPO, search, setSearch }) {
  const [statusFilter, setStatusFilter] = useState('');
  const [showFromQuote, setShowFromQuote] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const url = `/purchase-orders${statusFilter ? `?status=${statusFilter}` : ''}`;
  const { data, loading, refetch } = useApi(url, [statusFilter]);
  const allPos = data?.purchase_orders ?? [];
  const pos = search.trim()
    ? allPos.filter(p => {
        const q = search.toLowerCase();
        return p.po_ref.toLowerCase().includes(q)
          || (p.supplier_name || '').toLowerCase().includes(q)
          || (p.notes || '').toLowerCase().includes(q);
      })
    : allPos;

  const STATUS_TABS = ['', 'draft', 'sent', 'confirmed', 'partial', 'received', 'cancelled'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showFromQuote && (
        <QuotePOModal onClose={() => setShowFromQuote(false)} onCreated={po => { setShowFromQuote(false); refetch(); onSelectPO(po.id); }} />
      )}
      {showCreate && (
        <CreatePOModal
          onClose={() => setShowCreate(false)}
          onCreated={po => { setShowCreate(false); refetch(); onSelectPO(po.id); }}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {STATUS_TABS.map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              style={{ padding: '5px 12px', borderRadius: 'var(--radius)', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                background: statusFilter === s ? 'var(--pld-green)' : 'var(--surface-2)',
                color: statusFilter === s ? '#fff' : 'var(--text)' }}>
              {s ? PO_STATUS[s]?.label : 'All'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search PO ref, supplier, notes…"
            style={{ padding: '8px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', color: 'var(--text)', width: 240 }}
          />
          <Btn size="sm" variant="ghost" onClick={() => setShowFromQuote(true)}>📄 PO from quote</Btn>
          <Btn size="sm" onClick={() => setShowCreate(true)}>+ New PO</Btn>
        </div>
      </div>

      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
      : !pos.length ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
          {search.trim() ? `No purchase orders match "${search}".` : `No purchase orders${statusFilter ? ` with status "${statusFilter}"` : ''} yet.`}
        </p>
      : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                {['PO Ref', 'Supplier', 'Status', 'Order Date', 'Expected', 'Notes', ''].map(h => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pos.map((p, i) => (
                <tr key={p.id} onClick={() => onSelectPO(p.id)}
                  style={{ borderBottom: i < pos.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>{p.po_ref}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{p.supplier_name}</td>
                  <td style={{ padding: '10px 14px' }}><POBadge status={p.status} /></td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtDate(p.order_date)}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtDate(p.expected_delivery_date)}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>{p.notes || '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 16 }}>›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Default export ────────────────────────────────────────────────────────────
export default function Stock() {
  const [tab, setTab] = useState('Stock Items');
  // ?item=<id> opens that item (links from storage units).
  const [selectedItemId, setSelectedItemId] = useState(() => new URLSearchParams(window.location.search).get('item'));
  const [selectedPoId, setSelectedPoId] = useState(null);
  // Lifted out of StockList so they survive it unmounting while a detail
  // view is open — see the comment on StockList itself.
  const [stockSearch, setStockSearch] = useState('');
  const [stockFilterCat, setStockFilterCat] = useState('');
  const [poSearch, setPoSearch] = useState('');

  if (selectedItemId) return <ItemDetail itemId={selectedItemId} onBack={() => { setSelectedItemId(null); }} />;
  if (selectedPoId)   return <PODetail   poId={selectedPoId}   onBack={() => { setSelectedPoId(null); }} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <div style={{ padding: '0 0 0 0', borderBottom: '2px solid var(--border)', display: 'flex', gap: 2, marginBottom: 24 }}>
        {['Stock Items', 'Purchase Orders', 'Suppliers'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: tab === t ? 'var(--pld-green)' : 'var(--text-muted)', borderBottom: tab === t ? '2px solid var(--pld-green)' : '2px solid transparent', marginBottom: -2 }}>
            {t}
          </button>
        ))}
      </div>
      {tab === 'Stock Items'     && <StockList   onSelectItem={id => setSelectedItemId(id)} search={stockSearch} setSearch={setStockSearch} filterCat={stockFilterCat} setFilterCat={setStockFilterCat} />}
      {tab === 'Purchase Orders' && <POList      onSelectPO={id => setSelectedPoId(id)} search={poSearch} setSearch={setPoSearch} />}
      {tab === 'Suppliers'       && <SuppliersTab />}
    </div>
  );
}
